import {
  type ManualTransferJob,
  type ManualTransferOptions,
  type Readiness,
  releasableStopIds,
  type SafetyClearResponse,
  type SchedulerState,
  type StopItem,
  type StopsReleaseResult,
} from '@ctcells/api'
import type { Alarm, Batch, ModuleSnapshot, SafetyState, TrayUnload } from '@ctcells/contract'
import { batchStatusLabel, serverText, t } from '@ctcells/i18n'

import { occupiedPointsOf } from '../modules/moduleInitializeGate.ts'

/**
 * 개발용 「테스트 작업 시작」의 **앞 단계 — 자동 준비**(eric 2026-09-28 「그냥 다 초기화하고, 다 동작하게 한 다음에,
 * 그냥 실행하게 해줘. 왜 하나하나 다 누르고 있어?」).
 *
 * 사람이 모듈 서랍 · 작업 줄 · 관리 화면에서 하나씩 누르던 복구 순서를 **같은 계약 창구로** 차례대로 밟는다.
 * 🔴 **부르는 곳은 둘이다** — ① 개발용 「테스트 작업 시작」(`DevQuickBatch` · `VITE_DEV_QUICK_BATCH`) ② 작업 줄 [시작]의 시뮬 준비
 *    (`useSimStartPrepare` · 같은 플래그). 🔴 이 흐름은 진행 중 작업을 종착시키고 격리를 풀고(현장 확인 셋을 «했다»로 보낸다) 반출을
 *    확약한다. 둘 다 로컬 개발 DB 라서 허락된 지름길이다(eric 「테스트 작업이고 로컬 디비라 멈춰도 괜찮아」). 감사 사유 머리는 부르는 쪽이
 *    준다(`reasonHead`). 그 밖의 화면(작업 상세 · 시작 창 등)에 말없이 붙이지 마라.
 *    ⚠ 종전 ③ 시작 창의 [한 번에 준비](`shell/prepareAll.ts`)는 걷었다(2026-10-24 · eric 「전체 정리를 요청한 적이 없는데」 뒤 부르는 곳 0건).
 *
 * | 차례 | 단계 | 창구 | 건너뛰는 때 |
 * | --- | --- | --- | --- |
 * | 0 | 서버 준비 점검 | `GET /readyz`(루트 · 무인증) — `scheduler` · `log_sink` 밖 칸이 전부 성해야 · `GET /scheduler` 의 `state` 가 `ticking`(또는 `terminated` → 2단계가 해제) | 건너뛰지 않는다 |
 * | 1 | 제어락 | `POST /control-token` | 이미 쥐고 있다 |
 * | 2 | 스케줄러 종착 해제 | `GET /scheduler`(제어락 헤더) → `reset_allowed` 면 `POST /scheduler/reset` 한 번 → `ticking` 이 될 때까지 상한 안에서 다시 읽기 | 0단계가 `ticking` 을 읽었다 |
 * | 3 | 종착 안 한 작업 | `POST /batches/{id}/stop` · 서버 집합에 `stop` 이 없고 `recover` 가 있으면 `recover {decision:'discard'}` | `GET /batches?phase=active` 0건 |
 * | 4 | 반출 확약 | `POST /unloads/{id}/confirm` | `GET /unloads?pending=true` 0건 |
 * | 5 | 모듈 실행 취소 | `POST /modules/{id}/control/executions/{execution_id}/cancel` | `current_execution` 가진 모듈 0대 |
 * | 6 | 안전 해제 | `GET /safety` → `POST /safety/clear {command_id}` **한 번** → `GET /safety` 가 `normal` · `cleared:true` 가 될 때까지(상한 `QUICK_SAFETY_SETTLE_MS`) | 처음부터 `normal` · `cleared:true` |
 * | 7 | 이송 격리 | `GET /safety/stops` → `POST /safety/stops/release {ids: 풀 항목 전부, physical_checked: true}` **한 번** → 다시 읽어 `kind: 'quarantine'` 0건 | `kind: 'quarantine'` 0건 |
 * | 8 | 쥔 트레이 내려놓기 | `GET /transfers/manual/options` → `POST /transfers/manual {kind:'place_held', dest_point_id}` → `GET /transfers/manual/{job_id}` 가 `done` 이 될 때까지 | `HANDOFF` 모듈 0대 · 🔴 `keepBatchId` 자신이 내려놓기를 막으면(유일한 미종착 작업) 끊지 않고 결과 `deferred: 'heldTray'` 로 멈춘다 — 시작 창이 받는다 |
 * | 9 | 트레이 자리 정리 | `POST /modules/prepare` — `prepareRunGate` 를 **통과해야** 보낸다 | 점유(`handoff_occupancy[].occupied`) 0건 |
 * | 10 | 팔 이동 자세 복귀 | `sequences move_to_module {point_id, stage:'above'}` → `{point_id: travel-1}` — 이번 회차에 종착시킨 작업과 관련된(`context.execution_id` · `last_ts`) `ARM_NOT_IN_TRAVEL_POSE` 가 있는 6축 | 그런 알람 없음 |
 * | 11 | 모듈 초기화 | `POST /modules/{id}/control/initialize` — 도어가 열려 거절(`MODULE_HANDOFF_DOOR_OPEN`)되면 `close_door` 뒤 다시 · `ERROR`(또는 `MODULE_IN_ERROR`)면 복구 개시 `POST /modules/{id}/recovery/start`(래치면 `recovery/reset` 선행) · **파지 중**(`INITIALIZE_ROBOT_HOLDING_TRAY` · 종전 `MODULE_BUSY` · `context.reason: robot_holding_tray`)이고 6축 아님 · 실행 없음 · `grips[]` 에 `holding` 이면 `POST /modules/{id}/grips/forget {robot_id}` 뒤 초기화 **한 번만** 다시 | 전부 `IDLE` |
 * | 12 | IDLE 대기 | `GET /modules` 를 상수 간격 · 상한 시간 안에서만 | 처음부터 전부 `IDLE` |
 *
 * 🔴 **안전 해제(6) · 파지 기억(11)** — 2026-09-29 16:50 재부팅 뒤 이 버튼이 `MODULE_BUSY` 한 줄로 멈췄다(eric 17:05).
 *    팀장이 로컬에서 확인한 것(17:06~17:12): ⓐ 작업 중 재기동된 조립기가 `MOVING` · `current_execution: null` ·
 *    `grips: [{asm.cart-1, holding}]` 로 서 초기화가 `409 MODULE_BUSY · robot_holding_tray` · 6축이 쥔 게 아니라
 *    `place_held` 후보는 꺼져 있었다 ⓑ 그걸 풀려고 누른 E-STOP 으로 `GET /safety` 가 `emergency_stop` · 모듈 넷
 *    `hold.kind: safety_stop` · 6축 `SAFE_STOP` 이었고 이 흐름엔 안전을 보는 단계가 0건이었다.
 *    손으로 푼 순서 — `safety/clear` 200(실 모듈 넷 `cleared:true` · 꺼진 mock 넷 `COMM_UNREACHABLE` 인데 성립 · 6축은 서버가
 *    `/resume` 을 보내 IDLE) → 초기화 여전히 409 `robot_holding_tray` → `grips/forget {robot_id: asm.cart-1}` 200 → 초기화 202 → IDLE.
 *    쵸단 재확인(같은 날 · 로컬 `127.0.0.1:8000` · 이 흐름을 fetch 포트로 그대로 돌림):
 *    - 17:26 `POST /emergency-stop` 202 → `GET /safety` `emergency_stop` · 모듈 넷 `safety_stop` · 6축 `SAFE_STOP` → 흐름:
 *      작업 폐기 → `safety/clear` 200(mock 넷 `COMM_UNREACHABLE` 인데 성립) → 곧바로 읽은 `GET /safety` 는 `normal` ·
 *      `cleared:false`(「…안전정지 래치가 남아 있다(모듈 자기 보고)」) → 2초 뒤 `cleared:true` → 뒤 단계 통과 · 6축 IDLE.
 *      비상정지 중에도 작업 중지 · 폐기 · 실행 취소는 받혔다 — 움직이는 호출(초기화 · 시퀀스 · 이송)만 막힌다.
 *    - 17:30 원심 재기동(팀장 · holding 순간) → `MOVING` · 실행 없음 · `cent.cart-1 holding` → 초기화 409 `robot_holding_tray`
 *      → `grips/forget` 200 인데 🔴 **본문 스냅샷은 아직 `holding`** · 5ms 뒤 초기화 다시 409. ⇒ 지운 뒤 L2 미러에서 holding 이
 *      빠질 때까지 기다린다(`forgetHeld` 끝). 17:32 — 작업 중지 + 실행 취소 직후에도 같은 꼴이 섰고, 기다린 뒤 초기화 202 →
 *      새 작업 start 202 → `running` · 원심 실행 스텝 44 → 56 진행.
 *
 * 🔴 **화면은 요청만 보낸다**(DP-04). 「됐다」는 늘 **다시 읽은 서버 값**이 말한다 — 중지를 보냈다고 작업을 종착으로
 *    적지 않고, 초기화를 보냈다고 IDLE 로 적지 않는다. 그래서 단계마다 목록을 **다시 받는다.**
 * 🔴 **스케줄러(0 · 2)** — v0.9.1 에서 `GET /scheduler` 가 정식 창구가 됐다. `/readyz` 의 `checks.scheduler` 는 «이 프로세스»의
 *    틱 메모리라 판정에 쓰지 않는다. `terminated` 만 자동으로 푼다(개발용 버튼 한정 · 사유는 감사 원장에 봉인) — 해제는
 *    제어락이 있어야 해서(`reset_allowed`) 락 «뒤»에 한다. `stale` · `never_ran` · `not_started` 는 프론트가 풀 수 없다 — 멈춘다.
 * 🔴 **쥔 트레이 내려놓기(8)** — v0.9.1 에서 단건 `release` 는 모듈 `state` 를 바꾸지 않는다(06 `SM-MOD-21`) — 로봇이
 *    HANDOFF 에 남아 IDLE 대기가 늘 시간 초과였다. 수동 이송 `place_held` 가 규칙 걸음을 그대로 밟아 놓고 팔을 뺀다
 *    (실측 2026-09-29 — 약 67초 뒤 `done` · 로봇 IDLE · 그 로봇의 격리도 서버가 넘겨받아 풀었다).
 *    🔴 **판정은 서버가 한다** — 쥐었나 · 실행 중인가 · 목적 자리가 비었나는 후보 목록의 `enabled` · `code` · `reason` 이
 *    말한다. 놓을 자리는 **켜진 후보 안에서만** 고른다(서버 순서의 첫 후보 · v0.9.4 부터 — 머리말 「이송 격리(7)」). 꺼져 있으면 서버 사유로
 *    멈춘다 — 쥔 것이 없는 HANDOFF(`robot_not_holding`)도 그 갈래다(프론트가 풀 창구가 없다).
 * 🔴 **정리(9)는 내려놓기 «뒤»다** — 내려놓은 자리가 점유로 남으므로 그것까지 같이 푼다(실측 2026-09-29 — 정리가 두 자리를
 *    `/handoff/confirm` 으로 풀었다). 정리 뒤 원심분리기가 `WAITING_FOR_MANUAL_TRAY_UNLOAD` 에 남아 11단계 초기화가 여전히 필요하다.
 * 🔴 **팔 이동 자세 복귀(10)** — `grip`·`release` 는 그리퍼만 움직이고 팔은 핸드오프 지점 `at` 에 남는다. 그 채로 다음 배치가
 *    이동하면 모듈이 `ARM_NOT_IN_TRAVEL_POSE` 로 거절하고 배치가 paused 가 된다(실측 2026-09-28 · 5ec53f67). `/initialize` 도
 *    팔 장부를 안 고친다(`docs/backend-requests.csv` BR-100). 규칙에 맞는 복귀는 같은 지점 `above` → 이동 자세(module-core `arm_track.refuse()` ·
 *    팀장 대조 2026-09-28).
 *    🔴 **지점은 구조화 값에서만 읽는다** — 알람 문구 파싱 금지. 출처는 모듈 스냅샷의 핸드오프 자리(`armCandidates` ·
 *    v0.9.4 에서 종전 1차 출처 `tray` 락 `resource_id` 가 사라졌다). 후보가 여럿인 게 정상이라
 *    **차례로 시도한다**(팀장 결정 2026-09-28 · 개발용이라 헛걸음 허용 · 상한 `QUICK_ARM_CANDIDATES`). 한 후보의 `above` 가
 *    `ARM_NOT_IN_TRAVEL_POSE` · `AUX_STATION_NOT_REACHED` 로 거절되면(`armRefusal` — 1차 `context.execution_id` ·
 *    2차 알람 앞뒤 비교) 다음 후보, 다 실패하면 시도한 목록과 코드를 적고 멈춘다.
 * 🔴 **폴링은 IDLE 을 기다리는 자리에서만** — `QUICK_IDLE_POLL_MS` 간격 · `QUICK_IDLE_TIMEOUT_MS` 상한. 넘으면 어느 모듈이 어떤
 *    `state` 에 남았는지 적고 멈춘다.
 *    🔴 **실행 id 로 «끝남»을 읽는다** — v0.9.1 단건은 `state` 를 바꾸지 않으므로 IDLE 은 끝남의 근거가 아니다. 그 실행이
 *    스냅샷에 한 번이라도 보였다가 빠져야 끝났다. 끝내 안 보이면(두 읽기 사이에 끝났다) 상한까지 기다린 뒤 알람으로 판정한다.
 * 🔴 **한 단계가 실패하면 거기서 멈춘다.** 뒤 단계는 보내지 않고, 실패 줄에 무엇이 왔는지(코드 원문)를 적는다.
 *    자동 재시도는 초기화의 `MODULE_BUSY`(취소가 아직 가라앉는 중) 하나에만 `QUICK_INIT_ATTEMPTS` 번까지다.
 *    🔴 `MODULE_BUSY` 라도 `context.reason === 'robot_holding_tray'` 면 같은 초기화를 되풀이하지 않는다 — 쥔 로봇이
 *    풀릴 일이 없다. 내려놓을 길이 없는 파지 «기억»(모듈 안 로봇 · 실행 없음 · `holding`)만 `grips/forget` 으로 지우고
 *    초기화를 **한 번만** 다시 보낸다. 6축은 지우지 않는다 — 쥔 트레이는 8단계 `place_held` 가 출구다.
 * 🔴 **`ERROR` 모듈(11)** — v0.9.1 부터 `/initialize` · `/sequences` 가 `409 MODULE_IN_ERROR` 다. 출구는 복구 개시 한 사이클이고
 *    (`RECOVERY → IDLE | ERROR` 는 모듈 상태가 말한다), 래치가 걸렸으면 `recovery/reset` 이 먼저다(사유 필수).
 *    복구 개시가 도어 열림으로 거절되면 `close_door` 뒤 한 번만 다시 보낸다. `force` 는 싣지 않는다(사람의 도어 확인이라).
 * 🔴 **대상 모듈은 `service_state === 'active'`** — 서버 정리 연산(`module_prepare.module_targets`)과 같은 자다.
 *    부재를 `active` 로 읽지 않는다(`prepareModulesFlow.ts` 와 같은 규칙).
 * 🔴 **서버 준비 점검(0)** — 2026-09-29 재부팅 사고: API 가 DB 이름 해석보다 먼저 떠 스케줄러가 안 돌았다
 *    (`checks.scheduler: "not_started"`). 요청은 전부 받아서 준비 · 시료 · 작업 · 시작이 다 «성공»했는데 배치가
 *    `running` 인 채 모듈에 스텝이 하나도 안 나갔다. 🔴 그때 `/readyz` 는 **HTTP 200**(`degraded`)이었다 — 상태 코드가
 *    아니라 **본문 `checks`** 로 판정한다. 성하지 않은 칸이 하나라도 있으면 **제어락도 잡지 않고** 그 칸을 적고 멈춘다.
 *    스케줄러만은 v0.9.1 부터 `GET /scheduler` 의 `state` 로 가른다(위 「스케줄러」).
 *    못 읽으면(프록시 없음 → SPA `index.html` · 404 · 전송 오류) 역시 멈춘다 — 통과로 접지 않는다.
 *    프론트는 서버를 재기동할 수 없다 — 사유를 정확히 적는 데까지다.
 * 🔴 **시작 뒤 정체 감시**(`watchStartStall`) — 0단계를 지나도 시작 뒤에 스케줄러가 멎을 수 있다. 시작이 받아진 뒤
 *    `QUICK_STALL_MS` 안에 어느 모듈에도 실행이 안 붙으면 그 시점 `/readyz` 를 한 번 더 읽어 한 줄을 적는다.
 *    같은 읽기에서 그 작업이 `paused` · `aborted` · `error` 가 되면 곧바로 서버의 `halt_reason`(없으면 최근 활성 알람)을
 *    적는다. 작업을 끊거나 다시 시작하지 않는다. 다시 읽기는 이 감시 구간 안에서만 · `QUICK_STALL_POLL_MS` 간격이다.
 * 🔴 **이송 격리(7)** — v0.9.4 에서 서버가 자원 락(`GET /resource-locks` · `POST /resource-locks/{id}/release`)을 걷었다.
 *    격리는 이송 상태 기록(`handoff_record`)에서 계산되고, 푸는 창구는 «한 번에 풀기»(`IFC-RO-110`) 하나다.
 *    - **왜 남기나** — 9단계 정리(`POST /modules/prepare`)도 끝에 «정리된 격리»를 풀지만(`module_prepare.release_settled_quarantines`),
 *      ⓐ 정리는 점유가 있을 때만 보낸다 ⓑ 서버 규칙이 **배치가 있는 격리만** 본다(`o.batch_id is not None`) — 멈춘 수동 이송
 *      (`place_held` 등 · `job_id` 만 있다)이 남긴 격리는 정리가 영영 안 푼다(2026-10-08 15:26 ~ 16:16 작업 5건이 원심 → 6축
 *      넘김에서 `dispatch_halt` 로 멈춘 그 부류) ⓒ 로봇 미러가 낡았거나 빈손 제자리가 아니면 건너뛴다.
 *    - 🔴 **골라 풀 수 없다** — 서버가 요청 `ids` 를 지금의 «풀 항목» 집합(`releasable ∧ parent 없음`)과 맞대 다르면 409
 *      `STOPS_CHANGED` 다. 그래서 격리만이 아니라 그때 풀 항목 **전부**(안전 · 쥔 로봇 재개 · 문 닫기)가 같이 나간다.
 *      그래서 이 단계는 **안전 해제(6) «뒤»**다 — 작업 종착(3) · 실행 취소(5) 전에 안전이 풀리면 남은 것이 다시 움직인다.
 *    - 🔴 **무엇이 걸려 있든 푼다**(eric 2026-10-21 「상관없이 초기화하고. 무조건 시작하라고 했잖아」) — 파지를 모르는 격리(`grip_unknown`)도
 *      멈추지 않고 같이 푼다. 서버가 풀 항목 종류마다 현장 확인(`physical_checks`)을 요구하게 바뀌어, 이 흐름은 `physical_checked: true`
 *      (= 확인 셋 모두)로 보낸다. 로컬 개발 장비 전용이다 — 감사 원장에는 「테스트 작업 자동 준비」 사유와 함께 남는다.
 *      ⚠ 종전(~ 2026-10-20)엔 파지 모름에서 멈추고 늘 `false` 로 보냈다 — 서버 변경 뒤 그 길은 이송 격리 해제를 늘 422 로 막았다.
 *    - 내려놓기(8)의 `place_held` 는 **그 로봇의 격리**를 스스로 넘겨받는다(`manual_transfer._transfer_deny`) — 7이 먼저 풀어도 겹치지 않는다.
 *    - ⚠ 종전 「종착 작업이 쥔 트레이 자리」(락 `resource_id`)로 세우던 8단계 놓을 자리 우선순위 · 10단계 팔 복귀 후보는 출처가
 *      사라졌다. `StopItem` 은 자리를 구조화 값으로 싣지 않는다(`said` 는 문장이라 파싱 금지). 8은 서버 후보 순서, 10은 모듈
 *      스냅샷의 핸드오프 자리(`armCandidates`)로 고른다.
 */

/** IDLE 대기 상한. 넘으면 남은 모듈과 그 state 를 적고 멈춘다 */
export const QUICK_IDLE_TIMEOUT_MS = 90_000
/** IDLE 대기 중 `GET /modules` 를 다시 받는 간격 — 대기 단계 안에서만 돈다 */
export const QUICK_IDLE_POLL_MS = 2_000
/**
 * HANDOFF 로봇의 실행이 취소 뒤 스냅샷에서 빠지기를 기다리는 상한 — 5단계 취소는 202 뒤 비동기라 곧바로 읽으면 아직
 * 남아 있다(실측 2026-09-28 — 누른 직후 `exec-…` 가 남아 있었고 몇 초 뒤 `null`). 넘으면 멈춘다
 */
export const QUICK_RELEASE_SETTLE_MS = 15_000
/** 초기화가 `MODULE_BUSY` 로 거절될 때 다시 보내는 최대 횟수(첫 시도 포함) */
export const QUICK_INIT_ATTEMPTS = 3
/** 그 재시도 사이 간격 */
export const QUICK_INIT_RETRY_MS = 2_000

/**
 * 쥔 트레이 내려놓기 잡이 `done` 이 되기를 기다리는 상한 — 규칙 걸음(문 열기 · 슬라이드 · 놓기 · 팔 빼기)을 다 밟는다.
 * 실측 2026-09-29 약 67초라 IDLE 대기 상한(90초)으로는 여유가 모자라 따로 둔다. 넘으면 잡의 마지막 말을 적고 멈춘다
 * (잡을 중지하지 않는다 — 걸음 한가운데서 끊으면 격리가 선다)
 */
export const QUICK_PLACE_TIMEOUT_MS = 180_000
/**
 * 실행이 붙은 모듈이 그 실행을 마치기를 기다리는 상한 — 내려놓기 잡 `done` 뒤(그 로봇) · 초기화 «앞»(전부).
 * 🔴 **2026-10-13 14:39 시연 사고** — 서버는 트레이를 놓은 걸음에서 잡을 `done` 으로 적고, 6축은 그 뒤 대기 자리 복귀
 *    (`to_park`)를 한 걸음 더 돈다. 그 사이 이 흐름이 6축 · 조립기에 초기화를 보내 복귀가 끊겼고(`command_dedup` 14:39:04~05),
 *    이송이 «아는 멈춤»(`halted` · 격리 아님)으로 열린 채 남아 다음 작업의 첫 이송이 「수동 이송이 그 자원을 쓰고 있다」로 막혔다.
 *    같은 순서를 10-14 재현했을 때는 복귀가 초기화보다 먼저 끝나 통과했다 — 타이밍에 기대지 않게 기다린다.
 */
export const QUICK_EXEC_SETTLE_MS = 60_000
/**
 * 스케줄러 종착 해제 뒤 `ticking` 을 기다리는 상한 — 해제는 권위 표만 내리고 루프는 다음 주기에 읽는다. 그 사이
 * 다시 읽으면 `terminated`(메모리 모드) · `stale`(옛 마지막 성공)일 수 있다
 */
export const QUICK_SCHEDULER_SETTLE_MS = 15_000

/**
 * 안전 해제 뒤 `GET /safety` 가 `normal` · `cleared:true` 가 되기를 기다리는 상한. 해제 200 직후에는 모듈 미러가 아직
 * 안 따라와 잠깐 `cleared:false` + 「…안전정지 래치가 남아 있다(모듈 자기 보고)」였고 몇 초 뒤 풀렸다(팀장 실측 2026-09-29).
 * 넘으면 남은 미해소 조건 원문을 적고 멈춘다 — 해제를 다시 보내지 않는다
 */
export const QUICK_SAFETY_SETTLE_MS = 15_000

/** 시작이 받아진 뒤 모듈에 실행이 붙기를 기다리는 상한 — 넘으면 정체 한 줄 */
export const QUICK_STALL_MS = 30_000
/** 정체 감시 중 `GET /modules` 를 다시 받는 간격 — 감시 구간 안에서만 돈다 */
export const QUICK_STALL_POLL_MS = QUICK_IDLE_POLL_MS

/** 준비 판정의 성한 값 — 서버 `observability.PROBE_OK` */
const PROBE_OK = 'ok'
/**
 * 🔴 스케줄러 칸은 `/readyz` 로 판정하지 않는다 — 그 값은 «이 프로세스»의 틱 메모리다(다중 워커면 `standby`).
 *    권위는 `GET /scheduler` 의 `state` 다(v0.9.1 · 서버가 권위 표 + 마지막 성공 시각으로 계산). 여기서 빼고 0단계가 따로 읽는다.
 */
const SCHEDULER_KEY = 'scheduler'
/**
 * 🔴 로그 적재 칸(`log_sink`)은 판정하지 않는다(eric 2026-10-13 「log_sink 가 백엔드에서 체크하라고 한 거야? 그냥 떼..」).
 *    서버가 «이 칸이 성해야 일한다»고 요구한 게 아니다 — 우리가 `scheduler` 밖 칸을 전부 막았을 뿐이다.
 *    값은 `app_log` DB 쓰기 실패의 **누적** 관측치(`dropped N · failed M`)라 재기동 전까지 줄지 않는다.
 *    10-12 의 일시 TimeoutError 49번(유실 0) 하나로 자동 준비가 다음 날까지 막혔다. 경고 문구도 띄우지 않는다.
 *    정체 한 줄(`readinessSummary`)에는 진단용으로 원값이 그대로 남는다.
 */
const LOG_SINK_KEY = 'log_sink'
/** `/readyz` 에서 판정하지 않는 칸 — 스케줄러(권위 창구가 따로) · 로그 적재(누적 관측치) */
const READINESS_IGNORED: ReadonlySet<string> = new Set([SCHEDULER_KEY, LOG_SINK_KEY])
/** 스케줄러가 도는 중 — 계약 `SchedulerState.state` 원값 */
const SCHEDULER_TICKING = 'ticking'
/** 연속 실패 상한에서 종착 — 사람이 해제하는 유일한 갈래(`POST /scheduler/reset`) */
const SCHEDULER_TERMINATED = 'terminated'

/** 성하지 않은 칸 — `이름: 원값` 목록. 서버가 준 순서 그대로. 스케줄러 · 로그 적재 칸은 뺀다(`READINESS_IGNORED`) */
export function readinessProblems(checks: Readonly<Record<string, string>>): string[] {
  return Object.entries(checks)
    .filter(([key, value]) => !READINESS_IGNORED.has(key) && value !== PROBE_OK)
    .map(([key, value]) => `${key}: ${value}`)
}

/** 스케줄러 한 줄 — `state` 원값과 (있으면) 종착 까닭 원문 */
function schedulerText(state: SchedulerState): string {
  const reason = typeof state.terminated_reason === 'string' ? state.terminated_reason.trim() : ''
  return reason === '' ? state.state : `${state.state} · ${reason}`
}

/** 준비 상태 한 줄 — 읽었으면 칸 전부 `이름: 원값`, 못 읽었으면 그 진단 원문 */
function readinessSummary(readiness: Readiness): string {
  if (readiness.kind === 'unreadable') return `/readyz ${readiness.reason}`
  return Object.entries(readiness.checks)
    .map(([key, value]) => `${key}: ${value}`)
    .join(' · ')
}

/** 모듈이 쉬는 상태 — 계약 `ModuleStatus.state` 원값 */
const IDLE = 'IDLE'
/** 6축이 트레이를 쥔 구간 — 🔴 초기화하면 쥔 것을 떨어뜨린다(`SQ-HANDOFF-05` · 서버 정리 연산도 안 보낸다) */
const HANDOFF = 'HANDOFF'
/** 모듈 오류 상태 — 계약 `ModuleStatus.state` 원값. 초기화 · 시퀀스를 안 받는다(v0.9.1) */
const ERROR_STATE = 'ERROR'
/** 초기화 재시도 대상 코드 — 취소가 가라앉기 전 */
const BUSY_CODE = 'MODULE_BUSY'
/**
 * `MODULE_BUSY` 중 «로봇이 파지 중(쥠·모름)»의 `context.reason` — L2 가 초기화를 막는다(`module_prepare.ROBOT_HOLDING_TRAY`).
 * 🔴 기다려도 안 풀린다 — 재시도하지 않고 멈춘다
 */
const ROBOT_HOLDING_REASON = 'robot_holding_tray'
/**
 * 초기화 중계가 «로봇이 파지 중»으로 거절한 코드 — 지금 서버가 내는 값(`views/module_control.py` · `module_prepare.holds_tray` 판정).
 * 🔴 종전 `MODULE_BUSY · robot_holding_tray` 와 같은 갈래로 읽는다(히나 실측 2026-10-23 경우 0 — 이 코드를 몰라 파지 기억을 안 지우고 멈췄다)
 */
const HOLDING_TRAY_CODE = 'INITIALIZE_ROBOT_HOLDING_TRAY'
/** 파지 중 — 계약 `ModuleSnapshot.grips[].state` 원값 */
const GRIP_HOLDING = 'holding'
/** `ERROR` 모듈이 초기화 · 시퀀스를 거절한 코드 — 출구는 복구 개시다 */
const IN_ERROR_CODE = 'MODULE_IN_ERROR'
/**
 * 지령으로 연 도어가 열려 있어 초기화를 안 받는다 — 🔵 `close_door` 로 풀린다(`error_codes.yaml`
 * `MODULE_HANDOFF_DOOR_OPEN` · 「도어 시퀀스 자신은 받는다」). 실측 2026-09-28: 이송 중 중지된 원심분리기가
 * `TRAY_UNLOADING` · 후문 열림에 남았다.
 */
const DOOR_OPEN_CODE = 'MODULE_HANDOFF_DOOR_OPEN'
/** 도어 면 — `close_door` 스키마의 `door` enum. 거절 문맥이 면을 주면 그 값, 아니면 스키마 기본값(`rear`)에 맡긴다 */
const DOORS: ReadonlySet<string> = new Set(['rear', 'front'])

/** 거절 문맥(`ApiError.context`)의 문자열 칸 하나 — 없거나 문자열이 아니면 `undefined` */
function contextField(error: unknown, key: string): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined
  const context = (error as { context?: unknown }).context
  if (typeof context !== 'object' || context === null) return undefined
  const value = (context as Record<string, unknown>)[key]
  return typeof value === 'string' ? value : undefined
}

/** 거절 문맥의 배열 칸 하나 — 없거나 배열이 아니면 빈 배열 */
function contextList(error: unknown, key: string): unknown[] {
  if (typeof error !== 'object' || error === null) return []
  const context = (error as { context?: unknown }).context
  if (typeof context !== 'object' || context === null) return []
  const value = (context as Record<string, unknown>)[key]
  return Array.isArray(value) ? (value as unknown[]) : []
}

/** 안전 상태 정상 — 계약 `SafetyState` 원값. 🔴 `state` 와 `cleared` 둘 다 본다(`normal` 이어도 모듈 래치가 남으면 `cleared:false`) */
const SAFETY_NORMAL = 'normal'
export function safetySettled(safety: Pick<SafetyState, 'state' | 'cleared'>): boolean {
  return safety.state === SAFETY_NORMAL && safety.cleared
}

/**
 * 안전 해제 결과(`results[]` — 200 본문 · 409 `context.results`) 중 **살아 있는 모듈**이 못 푼 것 — `이름(코드)`.
 * 🔴 대상은 `activeModules()` 와 같은 자다(서버 성립 판정 `gating` 도 `service_state = active` 만 본다). 꺼진 모의 모듈의
 *    `COMM_UNREACHABLE` 은 여기서 빠진다(실측 2026-09-29 — mock-* 넷이 `cleared:false · COMM_UNREACHABLE` 인데 성립했다).
 */
export function unclearedModules(
  results: readonly unknown[],
  live: ReadonlyMap<string, string>,
): string[] {
  const out: string[] = []
  for (const row of results) {
    if (typeof row !== 'object' || row === null) continue
    const r = row as { module_id?: unknown; cleared?: unknown; error_code?: unknown }
    if (typeof r.module_id !== 'string' || r.cleared === true) continue
    const name = live.get(r.module_id)
    if (name === undefined) continue
    out.push(
      typeof r.error_code === 'string' && r.error_code !== '' ? `${name}(${r.error_code})` : name,
    )
  }
  return out
}

function doorOf(error: unknown): string | undefined {
  const door = contextField(error, 'door')
  return door !== undefined && DOORS.has(door) ? door : undefined
}

/**
 * 6축 이동 자세 지점 id — 계약 어휘다(`specs/vocab/positions.yaml` `collaborative_6axis.travel-1` · `kind: travel`).
 * L2 에 지점 등록부 조회 창구가 없어(`openapi.json` 2026-09-28) 어휘 상수로 둔다. 좌표가 아니라 이름이다.
 */
export const ROBOT_TRAVEL_POINT = 'collaborative_6axis.travel-1'
/** 수동 이송 잡 상태 — 계약 `ManualTransferJob.status` 원값 */
const JOB_RUNNING = 'running'
const JOB_DONE = 'done'

/** 서버 판정(꺼진 까닭) 한 줄 — 사람 말 원문과 카탈로그 코드. 둘 다 없으면 코드 자리만 비운다(지어내지 않는다) */
function verdictText(verdict: { code?: string | null; reason?: string | null }): string {
  const said = typeof verdict.reason === 'string' ? verdict.reason.trim() : ''
  const code = typeof verdict.code === 'string' ? verdict.code : ''
  if (said === '') return code
  return code === '' ? said : `${said} (${code})`
}

/** 6축 모듈 종류 — 계약 `module_type` 어휘 */
const ROBOT_TYPE = 'collaborative_6axis'
/** 팔이 이동 자세 밖이라 거절된 알람 코드(`error_codes.yaml`) — 코드로만 가른다 */
const ARM_POSE_CODE = 'ARM_NOT_IN_TRAVEL_POSE'
/**
 * 부가축(주행 · 승강)이 그 핸드오프 지점의 정거장에 없어 거절된 코드 — module-core `arm_track.refuse()` 의 `aux_wrong`.
 * 🔴 팔 자세가 아니라 **축 위치** 문제다. 가드가 `above`·`at` 모두 이 판정을 stage 판정보다 «먼저» 하므로
 *    (정거장이 있는 handoff 지점) 이 거절은 「팔이 그 후보 지점 안에 없다」로 읽힌다 — 팔이 at(Q) 에 있으려면
 *    축이 station(Q) 에 서 있어야 했고, 축은 팔이 이동 자세일 때만 움직인다(`move_aux_axis` D3b).
 *    ⇒ 「그 후보는 틀렸다」로 읽고 다음 후보로 넘어간다(실측 2026-09-29 11:48 — 부가축 park-1 · 후보 staining).
 */
const AUX_STATION_CODE = 'AUX_STATION_NOT_REACHED'
/** 팔 이동 한 번이 «거절됐다»로 읽는 코드 — 둘 다 모듈 자리 순서 가드(`arm_track.refuse`)가 낸다 */
const ARM_REFUSE_CODES: ReadonlySet<string> = new Set([ARM_POSE_CODE, AUX_STATION_CODE])

/**
 * 알람의 `context.execution_id` — 🔴 좁은 로컬 읽기. 이 파일을 쓸 때(2026-09-29) `@ctcells/contract` `Alarm` 에
 * `context` 칸이 아직 없었다(쵸단 `pnpm contract:gen` 재생성 중). 재생성이 들어오면 `Alarm['context']` 로 읽도록
 * 바꾸고 이 캐스팅을 걷는다. ⚠ 자유 객체라 문자열 아닌 값 · 빈 값은 «없음»이다.
 * ⚠ **알람의 `context` 는 그 행을 «처음» 세운 이벤트의 것이다** — 코어 `alarms.raise_alarm` 은 같은
 *   `grouping_key`(`{code}@{module}`) 재발 때 `occurrence_count` · `last_ts` · `version` 만 올리고 `event_id` 를
 *   안 바꾼다(`ctcells-core app/services/alarms.py` `_update_values` · 2026-09-29 대조). 그래서 이미 활성인 행이
 *   다시 울리면 `context.execution_id` 는 **옛 실행**을 가리킨다.
 */
function alarmExecutionId(alarm: Alarm): string | undefined {
  const context: unknown = (alarm as { context?: unknown }).context
  if (typeof context !== 'object' || context === null) return undefined
  const id = (context as { execution_id?: unknown }).execution_id
  return typeof id === 'string' && id !== '' ? id : undefined
}

/** 서버 시각 문자열 → epoch ms. 못 읽으면 `undefined`(순서를 지어내지 않는다) */
function serverTime(value: unknown): number | undefined {
  if (typeof value !== 'string') return undefined
  const at = Date.parse(value)
  return Number.isNaN(at) ? undefined : at
}

/**
 * 팔 이동 한 번이 거절됐는가 — 거절 코드(없으면 `undefined`). 이 로봇(`source`)의 거절 코드 알람만 본다.
 *
 * 1. 🔴 **1차** — `context.execution_id` 가 방금 보낸 실행 id 다 → 그 코드로 거절.
 * 2. 이동 «전»에 없던 행인데 `context.execution_id` 가 **다른** 실행이다 → 우리 거절이 아니다.
 * 3. **2차 폴백**(`occurrence_count:last_ts` 앞뒤 비교) — 다음 둘에서만 쓴다. 🔴 지우지 마라:
 *    - `context` 가 없는 알람(옛 서버 · `event_id` 가 없는 행)
 *    - 이동 «전»에 이미 활성이던 행 — 재발은 `context` 를 안 바꾸므로(`alarmExecutionId` 머리말) 실행 id 로는
 *      못 가른다. 실측 50건 중 12건만 `context` 에 실행 id 가 있었다(2026-09-29).
 *    ⚠ 우리 실행이 id 를 못 돌려받았으면(`executionId` 없음) 역시 폴백이다.
 */
export function armRefusal(
  before: readonly Alarm[],
  after: readonly Alarm[],
  robotId: string,
  executionId: string | undefined,
): string | undefined {
  const mine = (list: readonly Alarm[]) =>
    list.filter(
      (a) =>
        typeof a.error_code === 'string' &&
        ARM_REFUSE_CODES.has(a.error_code) &&
        String(a.source) === robotId,
    )
  const mark = (a: Alarm) => `${String(a.occurrence_count)}:${String(a.last_ts)}`
  const earlier = new Map(mine(before).map((a) => [String(a.alarm_id), mark(a)]))
  const candidates = mine(after)
  // 1차 — 실행 id 가 맞는 행
  const direct = candidates.find(
    (a) => executionId !== undefined && alarmExecutionId(a) === executionId,
  )
  if (direct !== undefined) return String(direct.error_code)
  for (const a of candidates) {
    const was = earlier.get(String(a.alarm_id))
    if (was === undefined) {
      // 새 행 — 그 context 는 «이번» 발생의 것이다. 다른 실행이면 우리 거절이 아니다
      if (executionId !== undefined && alarmExecutionId(a) !== undefined) continue
      return String(a.error_code)
    }
    // 2차 — 이미 있던 행이 다시 울렸다(context 는 첫 발생 것이라 못 믿는다)
    if (was !== mark(a)) return String(a.error_code)
  }
  return undefined
}

/**
 * 이번 회차(2단계)가 종착시킨 작업의 흔적 — 10단계 진입 판정의 자.
 * `since` 는 그 작업들 중 가장 이른 `started_at`(없으면 `created_at`) · `executions` 는 종착 «전» 모듈 스냅샷에서
 * 그 작업(`current_execution.batch_id`)에 붙어 있던 실행 id 와 5단계가 취소한 실행 id.
 */
interface RunTrace {
  since: number | undefined
  executions: Set<string>
}

/**
 * 🔴 10단계에 들어갈 «팔 거절» 알람인가 — 이 로봇의 `ARM_NOT_IN_TRAVEL_POSE` 가 **이번 회차에 종착시킨 작업과
 * 관련**될 때만: `context.execution_id` 가 그 작업의 실행이거나, `last_ts` 가 그 작업 시작 이후다.
 * 활성 목록에 옛 행이 남아 있다는 것만으로는 들어가지 않는다(2026-09-29 11:48 — 로봇이 이미 이동 자세 · 파킹인데
 * 옛 알람 때문에 팔 복귀를 탔고, 후보 `above` 가 `AUX_STATION_NOT_REACHED` 로 거절됐다).
 * ⚠ 시각은 서버 값끼리만 비교한다(`last_ts` ↔ `Batch.started_at`).
 */
export function armAlarmBelongsToRun(alarm: Alarm, trace: RunTrace): boolean {
  if (alarm.error_code !== ARM_POSE_CODE) return false
  const execution = alarmExecutionId(alarm)
  if (execution !== undefined && trace.executions.has(execution)) return true
  const last = serverTime(alarm.last_ts)
  return trace.since !== undefined && last !== undefined && last >= trace.since
}
/** 걸린 것 목록의 이송 격리 — 계약 `StopItem.kind` 어휘(`services/stops.py` `KIND_QUARANTINE`) */
const QUARANTINE_KIND = 'quarantine'

/** 감사 원장에 남는 사유 머리 — 사람이 이 흐름이 한 일임을 가려 읽는다 */
export const QUICK_RESET_REASON = '테스트 작업 자동 준비'

export type QuickResetStep =
  | 'ready'
  | 'lock'
  | 'scheduler'
  | 'batches'
  | 'unloads'
  | 'executions'
  | 'safety'
  | 'quarantine'
  | 'release'
  | 'prepare'
  | 'arm'
  | 'initialize'
  | 'idle'

/**
 * 🔴 `prepare` 는 `release` «뒤»다(2026-09-29) — 내려놓기가 트레이를 목적 자리에 두어 점유가 새로 선다.
 *    `scheduler` 는 `lock` «뒤»다 — 해제가 제어락을 요구한다.
 * 🔴 `quarantine` 은 `safety` «뒤»다(v0.9.4) — 한 번에 풀기가 그때 풀 항목 전부(안전 포함)를 같이 푼다.
 * 🔴 `safety` 는 `executions` «뒤» · `release` «앞»이다(2026-09-29) — 해제는 로봇을 재개(`/resume`)시키고 스케줄러
 *    발행 차단도 푼다. 작업을 종착시키고 실행을 취소하기 «전»에 풀면 남은 작업 · 실행이 그 사이 다시 움직인다.
 *    뒤 단계(내려놓기 · 팔 복귀 · 초기화)는 움직이는 호출이라 비상정지 중엔 서버가 거절한다(`module_control.MOVING`).
 */
export const QUICK_RESET_ORDER: readonly QuickResetStep[] = [
  'ready',
  'lock',
  'scheduler',
  'batches',
  'unloads',
  'executions',
  'safety',
  'quarantine',
  'release',
  'prepare',
  'arm',
  'initialize',
  'idle',
]

export type QuickLineStatus = 'running' | 'done' | 'skipped' | 'failed'

/** 진행 줄 한 개 — 🔴 모델만 낸다. 화면(`DevQuickBatch`)은 그리지 않는다(eric 2026-09-28 「그냥 내부적으로 돌려서 시작하라고」) — 실패 시 `QuickResetResult` 의 한 줄만 쓴다 */
export interface QuickLine {
  step: QuickResetStep
  status: QuickLineStatus
  text: string
}

/** 흐름이 부르는 창구 — 전부 서버 요청이거나 서버 값을 다시 읽는 것이다. 시험이 가짜로 바꿔 끼운다 */
export interface QuickResetPorts {
  /** `GET /readyz` — 던지지 않는다. 못 읽으면 `unreadable`(`@ctcells/api` `fetchReadiness`) */
  readReadiness: () => Promise<Readiness>
  /** `GET /scheduler` — 제어락을 쥐었으면 헤더가 실려 `reset_allowed` 가 이 요청자 기준이 된다 */
  readScheduler: () => Promise<SchedulerState>
  /** `POST /scheduler/reset` — 사유 필수(감사 봉인) */
  resetScheduler: (reason: string) => Promise<void>
  /** `GET /safety` — 단발 조회 */
  readSafety: () => Promise<SafetyState>
  /**
   * `POST /safety/clear` — 제어락 필요. 서버가 물리 가드 → 모듈 fan-out → (전이가 있으면) 로봇 재개까지 한다.
   * 🔴 거절은 409(`SAFETY_NOT_CLEARED` · `context.{unresolved, results}` / `SAFETY_TERMINAL_SERVICE_REQUIRED`)로 던진다
   */
  clearSafety: () => Promise<SafetyClearResponse>
  /**
   * `POST /modules/{id}/grips/forget {command_id, reason, robot_id}` — 파지 «기억»만 지운다(가동부 안 움직임).
   * 🔴 내려놓을 길이 없는 기억에만 쓴다 — 6축이 쥔 트레이(HANDOFF)는 8단계 `place_held` 다
   */
  forgetGrips: (moduleId: string, robotId: string, reason: string) => Promise<void>
  /** 지금 이 창이 제어락을 쥐었나 — 보관소의 현재 값(렌더 시점 값이 아니다) */
  lockHeld: () => boolean
  acquireLock: () => Promise<void>
  /** `GET /batches?phase=active` — 진행 중(종착 아님) 판정은 서버가 한다 */
  listActiveBatches: () => Promise<readonly Batch[]>
  /**
   * `GET /batches/{id}` — 시작 뒤 감시가 그 작업 «하나»를 읽는다. 목록(`phase=active`)은 종착 작업을 빼서
   * `aborted` · `error` 를 못 보인다. 🔴 `halt_reason` 은 계약 밖 칸이라 없을 수 있다(`@ctcells/contract` `Batch`)
   */
  readBatch: (batchId: string) => Promise<Batch>
  stopBatch: (batchId: string, reason: string) => Promise<void>
  discardBatch: (batchId: string, reason: string) => Promise<void>
  /** `GET /safety/stops` 의 `items` — 걸린 것 전부(풀 항목 · 사람 몫 · 하위 줄). 판정은 서버 칸(`releasable` · `grip_unknown`)이 한다 */
  listStops: () => Promise<readonly StopItem[]>
  /**
   * `POST /safety/stops/release` — 제어락 필요. 🔴 `ids` 는 지금 «풀 항목» 전부여야 한다(아니면 409 `STOPS_CHANGED`).
   * `physical_checked` 는 늘 `true` 로 보낸다(무조건 시작 · 머리말 「이송 격리(7)」). 일부 실패도 200 이다 — 항목별 `done`
   */
  releaseStops: (ids: readonly string[], reason: string) => Promise<StopsReleaseResult>
  /** `GET /unloads?pending=true` */
  listPendingUnloads: () => Promise<readonly TrayUnload[]>
  confirmUnload: (unloadId: string) => Promise<void>
  /**
   * 「트레이 자리 정리」 게이트 — `prepareRunGate` 를 **그대로** 거친 결과. 막히면 사유.
   * 🔴 게이트를 우회하지 않는다 — 2단계가 작업을 종착시켰기에 통과하는 것이다.
   */
  prepareBlocked: (activeBatches: readonly Batch[]) => string | undefined
  prepare: (reason: string) => Promise<void>
  /** `GET /modules` 전체 — 대상 거르기는 흐름이 한다 */
  listModules: () => Promise<readonly ModuleSnapshot[]>
  cancelExecution: (moduleId: string, executionId: string, reason: string) => Promise<void>
  /**
   * `POST /modules/{id}/control/initialize` — `rotorStopConfirmed` 는 그 모듈 스냅샷이 `exit_blockers` 에 `rotor_stop_unconfirmed` 를
   * 실었을 때만 참이다(원심이 중단 뒤 원점 복귀하려면 사람의 로터 정지 확인이 필요하다 · 06 `SM-MOD-23`).
   * 이 흐름을 부르는 사람이 그 확인을 했다 — 개발용 흐름(로컬 시뮬레이터)의 지름길
   */
  initialize: (moduleId: string, options?: { rotorStopConfirmed?: boolean }) => Promise<void>
  /** `GET /transfers/manual/options` — 후보와 후보마다 켜짐 · 까닭(서버 판정) */
  readPlaceOptions: () => Promise<ManualTransferOptions>
  /** `POST /transfers/manual {kind:'place_held', dest_point_id}` — 202 잡 */
  placeHeld: (destPointId: string, reason: string) => Promise<ManualTransferJob>
  /** `GET /transfers/manual/{job_id}` */
  readManualTransfer: (jobId: string) => Promise<ManualTransferJob>
  /** `POST /modules/{id}/recovery/reset` — 사유 필수 */
  resetRecoveryLatch: (moduleId: string, reason: string) => Promise<void>
  /** `POST /modules/{id}/recovery/start` — 사유 필수 · `force` 는 싣지 않는다 */
  startRecovery: (moduleId: string, reason: string) => Promise<void>
  /** `GET /alarms?state=active` — 코드 · 출처 · 시각으로만 읽는다 */
  listActiveAlarms: () => Promise<readonly Alarm[]>
  /**
   * 그 모듈이 낸 **해제 전** 알람 — `GET /alarms?source={id}&state=active` ∪ `state=acknowledged`.
   * 🔴 활성만 보면 안 된다: 확인(ack)된 행이 다시 울리면 `occurrence_count` · `last_ts` 만 오르고 **`acknowledged`
   *    그대로다**(코어 `_update_values` — 등급 상승 때만 `active` 로 되돌린다). 2026-09-29 11:48 팔 복귀의
   *    `AUX_STATION_NOT_REACHED` 가 바로 그 행(`acknowledged` · 8회)이라 활성 목록으로는 거절이 안 보였다.
   */
  listRobotAlarms: (moduleId: string) => Promise<readonly Alarm[]>
  /**
   * 모듈 시퀀스 중계 — 팔 복귀 `move_to_module` · 핸드오프 개방 도어의 `close_door`.
   * 202 의 `execution_id` 를 돌려준다(없으면 `undefined`)
   */
  runSequence: (
    moduleId: string,
    sequence: string,
    params: Record<string, unknown>,
  ) => Promise<string | undefined>
  /** 사람이 읽는 모듈 이름(화면 언어). 모르면 id */
  moduleName: (module: ModuleSnapshot) => string
  /** 감사 사유에 넣는 모듈 이름 — 언어와 상관없이 같은 글자다. 없으면 `moduleName` */
  moduleReasonName?: (module: ModuleSnapshot) => string
  /** 실패 사유 — 사전 문구 + 코드 원문 */
  failure: (error: unknown) => string
  sleep: (ms: number) => Promise<void>
  now: () => number
}

export type QuickResetResult =
  | { ok: true }
  | {
      ok: false
      step: QuickResetStep
      reason: string
      /**
       * `heldTray` — 6축이 쥔 트레이를 내려놓을 길을 «남기는 작업»(`keepBatchId`) 자신이 막는다. 작업을 끊는 것은 사람이 시작 창에서
       * [작업 중단 후 내려놓기]를 누를 때뿐이라(eric 2026-10-24) 이 흐름은 거기서 멈추고 시작 창으로 넘긴다(`useSimStartPrepare`)
       */
      deferred?: 'heldTray' | undefined
    }

/** 자동 준비의 갈래 — 비우면 「테스트 작업 시작」 그대로(진행 중 작업 전부 종착) */
export interface QuickResetOptions {
  /** 이 작업은 종착시키지 않고 남긴다 — 작업 줄 [시작]의 시뮬 준비(`useSimStartPrepare`) */
  keepBatchId?: string | undefined
  /**
   * 감사 사유 머리 — 단계마다 `${머리} · ${무엇}` 으로 원장에 남는다. 비우면 `QUICK_RESET_REASON`(「테스트 작업 자동 준비」).
   * 원장에서 누가 왜 했는지 가려 읽게 부르는 쪽이 정한다
   */
  reasonHead?: string | undefined
}

/** 끝난 흐름의 줄 목록 — 실패한 뒤의 단계는 「건너뜀 — 앞 단계에서 멈춤」으로 채운다 */
function skipRest(lines: QuickLine[], from: QuickResetStep): QuickLine[] {
  const index = QUICK_RESET_ORDER.indexOf(from)
  return [
    ...lines,
    ...QUICK_RESET_ORDER.slice(index + 1).map((step) => ({
      step,
      status: 'skipped' as const,
      text: t('dev.quickReset.notReached', { step: t(`dev.quickReset.step.${step}`) }),
    })),
  ]
}

class StepFailure extends Error {
  constructor(
    readonly reason: string,
    readonly deferred?: 'heldTray',
  ) {
    super(reason)
  }
}

function errorCodeOf(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined
  const code = (error as { error_code?: unknown }).error_code
  return typeof code === 'string' ? code : undefined
}

function shortId(value: string): string {
  return value.slice(0, 8)
}

/** 🔴 대상 모듈 — 서버 정리 연산과 같은 자(`service_state === 'active'`). 부재는 대상이 아니다 */
/** 모듈에 실행이 붙어 있나 — 스냅샷의 `current_execution.execution_id` 가 말한다 */
export function hasExecution(module: ModuleSnapshot): boolean {
  const id = module.current_execution?.execution_id
  return typeof id === 'string' && id !== ''
}

export function activeModules(modules: readonly ModuleSnapshot[]): ModuleSnapshot[] {
  return modules.filter((module) => module.service_state === 'active')
}

/** IDLE 이 아닌 대상 모듈 — 판정은 서버 `state` 원값 그대로 */
export function busyModules(modules: readonly ModuleSnapshot[]): ModuleSnapshot[] {
  return activeModules(modules).filter((module) => module.state !== IDLE)
}

/** 서버 집합으로 작업을 어떻게 종착시킬지 — `stop` 이 먼저, 없으면 `recover`(폐기), 둘 다 없으면 `undefined` */
export function terminationOf(batch: Batch): 'stop' | 'discard' | undefined {
  const allowed: readonly string[] = batch.allowed_commands ?? []
  if (allowed.includes('stop')) return 'stop'
  if (allowed.includes('recover')) return 'discard'
  return undefined
}

/** 팔 복귀 지점 후보 상한 — 헛걸음은 개발용 버튼이라 허용하되 이 수를 넘지 않는다(팀장 결정 2026-09-28) */
export const QUICK_ARM_CANDIDATES = 3

/** 핸드오프 자리 한 칸 — `handoff_occupancy[]` 실물 모양(`{point_id, occupied, basis, updated_at}` · 2026-09-28) */
interface OccupancyEntry {
  point: string
  occupied: boolean
  /** epoch ms. 못 읽으면 0 */
  at: number
}

function occupancyOf(modules: readonly ModuleSnapshot[]): Map<string, OccupancyEntry> {
  const out = new Map<string, OccupancyEntry>()
  for (const module of modules) {
    const raw: unknown = module.handoff_occupancy
    if (!Array.isArray(raw)) continue
    for (const entry of raw as unknown[]) {
      if (typeof entry !== 'object' || entry === null) continue
      const row = entry as { point_id?: unknown; occupied?: unknown; updated_at?: unknown }
      if (typeof row.point_id !== 'string' || row.point_id === '') continue
      const at = typeof row.updated_at === 'string' ? Date.parse(row.updated_at) : Number.NaN
      out.set(row.point_id, {
        point: row.point_id,
        occupied: row.occupied === true,
        at: Number.isNaN(at) ? 0 : at,
      })
    }
  }
  return out
}

/**
 * 팔 복귀 지점 후보 — 🔴 구조화 값만. 모듈 스냅샷의 핸드오프 자리 전체(`handoff_occupancy[].point_id`).
 * 정렬: ① 점유된 자리가 앞(트레이가 놓인 곳이 로봇이 마지막으로 일한 자리일 확률이 높다 · 팀장 결정 2026-09-28)
 * ② 그 자리의 `updated_at` 이 최근인 것이 앞(가장 최근에 손이 간 자리) ③ 이름 순. 상한 `QUICK_ARM_CANDIDATES`.
 * ⚠ 종전 1차 출처(종착 작업이 쥔 `tray` 락 자리)는 v0.9.4 에서 자원 락과 함께 사라졌다 — 머리말 「이송 격리(7)」.
 */
export function armCandidates(modules: readonly ModuleSnapshot[]): string[] {
  const seen = occupancyOf(modules)
  const pool = [...seen.keys()]
  const rank = (point: string) => seen.get(point) ?? { point, occupied: false, at: 0 }
  return pool
    .sort((a, b) => {
      const ra = rank(a)
      const rb = rank(b)
      return Number(rb.occupied) - Number(ra.occupied) || rb.at - ra.at || a.localeCompare(b)
    })
    .slice(0, QUICK_ARM_CANDIDATES)
}

/**
 * **자동 준비를 차례대로 밟는다.** 줄이 바뀔 때마다 `onLines` 로 **전체 목록**을 넘긴다(화면은 받은 것을 그린다).
 * 결과가 `ok: false` 면 그 단계에서 멈췄고, 뒤 단계는 하나도 보내지 않았다.
 */
export async function runQuickReset(
  basePorts: QuickResetPorts,
  onLines: (lines: readonly QuickLine[]) => void,
  options: QuickResetOptions = {},
): Promise<QuickResetResult> {
  /*
   * 🔴 **`keepBatchId` — 그 작업은 «남긴다»**(작업 줄 [시작]의 시뮬 준비 · eric 2026-10-14 「시뮬레이션 상황에서만 이거 통과하게」).
   *    3단계(종착 안 한 작업)가 그 작업을 중지 · 폐기하지 않는다 — 서버의 진행 중 목록에서 그 한 건만 뺀다.
   *    🔴 9단계(트레이 자리 정리)는 **아예 보내지 않는다** — 서버 정리가 비종착 작업을 전부 중단시켜 그 작업까지 끝낸다(2026-10-22 실측).
   */
  const keep = options.keepBatchId
  const ports: QuickResetPorts =
    keep === undefined
      ? basePorts
      : {
          ...basePorts,
          listActiveBatches: async () =>
            (await basePorts.listActiveBatches()).filter((batch) => batch.batch_id !== keep),
        }
  let lines: QuickLine[] = []
  const put = (line: QuickLine) => {
    lines = [...lines.filter((one) => one.step !== line.step), line]
    onLines(lines)
  }
  const running = (step: QuickResetStep, text: string) => put({ step, status: 'running', text })
  const done = (step: QuickResetStep, text: string) => put({ step, status: 'done', text })
  const skipped = (step: QuickResetStep, text: string) => put({ step, status: 'skipped', text })

  /**
   * 실행이 붙은 모듈이 그 실행을 마치기를 상한(`QUICK_EXEC_SETTLE_MS`) 안에서 기다린다 — `only` 가 있으면 그 모듈만.
   * 끝내 안 끝나면 `strict` 일 때 그 모듈과 실행 id 로 멈추고, 아니면 그대로 돌려준다(부르는 쪽이 종전 동작으로 간다).
   */
  const settleExecutions = async (
    step: QuickResetStep,
    { only, strict }: { only?: (module: ModuleSnapshot) => boolean; strict: boolean },
  ): Promise<ModuleSnapshot[]> => {
    const started = ports.now()
    for (;;) {
      const busy = activeModules(await ports.listModules()).filter(
        (module) => hasExecution(module) && (only === undefined || only(module)),
      )
      if (busy.length === 0) return []
      const names = busy
        .map(
          (module) =>
            `${ports.moduleName(module)} ${String(module.current_execution?.execution_id ?? '')}`,
        )
        .join(', ')
      if (ports.now() - started >= QUICK_EXEC_SETTLE_MS) {
        if (strict)
          throw new StepFailure(
            t('dev.quickReset.settle.timeout', {
              seconds: String(Math.round(QUICK_EXEC_SETTLE_MS / 1000)),
              modules: names,
            }),
          )
        return busy
      }
      running(step, t('dev.quickReset.settle.running', { modules: names }))
      await ports.sleep(QUICK_IDLE_POLL_MS)
    }
  }
  const reason = (what: string) => `${options.reasonHead ?? QUICK_RESET_REASON} · ${what}`
  const reasonName = (module: ModuleSnapshot) =>
    (ports.moduleReasonName ?? ports.moduleName)(module)
  const readStops = async (): Promise<readonly StopItem[]> => {
    try {
      return await ports.listStops()
    } catch (error) {
      throw new StepFailure(`/safety/stops ${ports.failure(error)}`)
    }
  }
  /**
   * 남은 이송 격리를 «한 번에 풀기»로 푼다 — 7단계와 마지막 관문(12)이 같이 쓴다. 푼 격리 수를 돌려준다(없으면 0 · 아무것도 안 보낸다).
   * 🔴 「풀렸다」는 다시 읽은 목록이 말한다 — 200 이라도 항목별 `done` 이 거짓이거나 다시 읽어 남았으면 멈춘다.
   */
  const releaseQuarantines = async (step: QuickResetStep): Promise<number> => {
    const items = await readStops()
    const held = items.filter((item) => item.kind === QUARANTINE_KIND)
    if (held.length === 0) return 0
    // 🔴 서버 규칙 그대로(`releasable ∧ parent 없음` · 정의처 `releasableStopIds`) — 부분집합이면 409 `STOPS_CHANGED`
    const ids = releasableStopIds(items)
    running(
      step,
      t('dev.quickReset.quarantine.running', {
        count: String(held.length),
        total: String(ids.length),
      }),
    )
    let result: StopsReleaseResult
    try {
      result = await ports.releaseStops(ids, reason(serverText('dev.quickReset.quarantine.reason')))
    } catch (error) {
      throw new StepFailure(ports.failure(error))
    }
    const failed = result.items.filter((item) => !item.done)
    if (failed.length > 0)
      throw new StepFailure(
        t('dev.quickReset.quarantine.notDone', {
          items: failed
            .map((item) => (item.error_code ? `${item.said} (${item.error_code})` : item.said))
            .join(' / '),
        }),
      )
    const left = (await readStops()).filter((item) => item.kind === QUARANTINE_KIND)
    if (left.length > 0)
      throw new StepFailure(
        t('dev.quickReset.quarantine.left', { items: left.map((item) => item.said).join(' / ') }),
      )
    return held.length
  }
  /** 0단계가 스케줄러 `terminated` 를 읽었다 — 2단계(락 뒤)가 해제한다 */
  let schedulerTerminated = false
  /** 3단계가 종착시킨 작업의 흔적 — 10단계 진입 판정(`armAlarmBelongsToRun`) */
  const trace: RunTrace = { since: undefined, executions: new Set() }
  /**
   * 팔 이동 한 번 — 보내고, 그 실행이 끝날 때까지 기다린 뒤 **거절 코드**를 돌려준다(받혔으면 `undefined`).
   * 🔴 거절은 동기 409 가 아니라 실행 중 알람으로 온다. 판정은 `armRefusal` — 1차 `context.execution_id`,
   *    2차 폴백 `occurrence_count:last_ts` 앞뒤 비교. 알람은 **해제 전 전부**(활성 + 확인됨)로 읽는다.
   * 🔴 **«끝남»은 실행이 스냅샷에 보였다가 빠진 것이다** — v0.9.1 단건은 `state` 를 안 바꿔 IDLE 이 근거가 못 된다.
   *    실행 id 가 없으면 그 모듈의 어떤 실행이든 한 번 보였다가 빠져야 한다. 상한까지 끝내 안 보이면(두 읽기 사이에
   *    끝났다) 그때 알람으로 판정한다 — 곧바로 「끝남」으로 읽지 않는다. 기다리는 동안 거절이 먼저 보이면 곧바로 돌려준다.
   */
  const moveAndCheck = async (
    robot: ModuleSnapshot,
    name: string,
    params: Record<string, unknown>,
  ): Promise<string | undefined> => {
    const before = await ports.listRobotAlarms(robot.module_id)
    let executionId: string | undefined
    try {
      executionId = await ports.runSequence(robot.module_id, 'move_to_module', params)
    } catch (error) {
      throw new StepFailure(`${name} move_to_module ${ports.failure(error)}`)
    }
    const refusal = async () =>
      armRefusal(before, await ports.listRobotAlarms(robot.module_id), robot.module_id, executionId)
    const started = ports.now()
    let seen = false
    for (;;) {
      await ports.sleep(QUICK_IDLE_POLL_MS)
      const now = (await ports.listModules()).find((m) => m.module_id === robot.module_id)
      const current = now?.current_execution?.execution_id
      const mine =
        typeof current === 'string' &&
        current !== '' &&
        (executionId === undefined || current === executionId)
      if (mine) seen = true
      else if (seen) return refusal()
      const refused = await refusal()
      if (refused !== undefined) return refused
      if (ports.now() - started >= QUICK_RELEASE_SETTLE_MS) {
        // 한 번도 못 봤으면 두 읽기 사이에 끝났다 — 알람이 없었으니 받혔다. 보였는데 아직 돌면 시간 초과다
        if (!seen) return undefined
        throw new StepFailure(
          t('dev.quickReset.idle.timeout', {
            seconds: String(Math.round(QUICK_RELEASE_SETTLE_MS / 1000)),
            modules: `${name} ${String(now?.state)}`,
          }),
        )
      }
      running('arm', t('dev.quickReset.arm.waiting', { module: name, state: String(now?.state) }))
    }
  }
  /**
   * 한 모듈이 IDLE 로 올 때까지 — 상한 안에서만 다시 읽는다.
   * `failOnError` 면 `ERROR` 를 한 번 벗어났다가 다시 `ERROR` 로 오면 곧바로 멈춘다(복구 한 사이클이 실패한 것)
   */
  const waitIdle = async (
    step: QuickResetStep,
    moduleId: string,
    name: string,
    failOnError = false,
  ) => {
    const started = ports.now()
    let leftError = false
    for (;;) {
      const now = (await ports.listModules()).find((m) => m.module_id === moduleId)
      if (now?.state === IDLE) return
      if (failOnError) {
        if (now?.state !== ERROR_STATE) leftError = true
        else if (leftError)
          throw new StepFailure(t('dev.quickReset.recovery.failed', { module: name }))
      }
      if (ports.now() - started >= QUICK_IDLE_TIMEOUT_MS)
        throw new StepFailure(
          t('dev.quickReset.idle.timeout', {
            seconds: String(Math.round(QUICK_IDLE_TIMEOUT_MS / 1000)),
            modules: `${name} ${String(now?.state)}`,
          }),
        )
      running(step, t('dev.quickReset.arm.waiting', { module: name, state: String(now?.state) }))
      await ports.sleep(QUICK_IDLE_POLL_MS)
    }
  }
  /**
   * `ERROR` 모듈 복구 — 래치면 해제 → 복구 개시(도어 열림이면 `close_door` 뒤 한 번 더) → IDLE 대기.
   * 🔴 한 사이클만 보낸다(DP-02 · 자동 복구 아님). 다시 `ERROR` 로 오면 멈춘다.
   */
  const recover = async (module: ModuleSnapshot, name: string) => {
    running('initialize', t('dev.quickReset.recovery.running', { module: name }))
    if (module.recovery?.locked === true) {
      try {
        await ports.resetRecoveryLatch(
          module.module_id,
          reason(serverText('dev.quickReset.recovery.latchReason', { module: reasonName(module) })),
        )
      } catch (error) {
        throw new StepFailure(`${name} recovery/reset ${ports.failure(error)}`)
      }
    }
    const why = reason(serverText('dev.quickReset.recovery.reason', { module: reasonName(module) }))
    try {
      await ports.startRecovery(module.module_id, why)
    } catch (error) {
      if (errorCodeOf(error) !== DOOR_OPEN_CODE)
        throw new StepFailure(`${name} recovery/start ${ports.failure(error)}`)
      const door = doorOf(error)
      try {
        await ports.runSequence(module.module_id, 'close_door', door === undefined ? {} : { door })
      } catch (closeError) {
        throw new StepFailure(`${name} close_door ${ports.failure(closeError)}`)
      }
      await ports.sleep(QUICK_INIT_RETRY_MS)
      try {
        await ports.startRecovery(module.module_id, why)
      } catch (again) {
        throw new StepFailure(`${name} recovery/start ${ports.failure(again)}`)
      }
    }
    await waitIdle('initialize', module.module_id, name, true)
  }

  /**
   * 초기화가 «파지 중»(`INITIALIZE_ROBOT_HOLDING_TRAY` · 종전 `MODULE_BUSY` · `robot_holding_tray`)으로 막힌 모듈의 파지 **기억**을 지운다.
   * 🔴 조건을 좁게 — 6축이 아니다(6축이 쥔 트레이는 내려놓기(`place_held`)가 출구 · 기억을 지우면 트레이가 장부에서
   *    사라진다) · `HANDOFF` 가 아니다 · 도는 실행이 없다(취소가 가라앉기를 상한 안에서 기다린다) · `grips[]` 에
   *    `holding` 이 있다. 하나라도 어긋나면 보내지 않고 그 사유로 멈춘다.
   * 실측(팀장 2026-09-29 17:06~17:12) — 작업 중 재기동된 조립기가 `MOVING` · 실행 없음 · `asm.cart-1 holding` 으로 섰고,
   * `grips/forget {robot_id: asm.cart-1}` 200 뒤 `at_target` · 초기화 202 · 3초 안에 `IDLE`.
   * 🔴 지운 뒤에는 L2 미러에서 `holding` 이 빠질 때까지 상한(`QUICK_RELEASE_SETTLE_MS`) 안에서 기다린다 — 아래 끝 줄.
   */
  const forgetHeld = async (module: ModuleSnapshot, name: string, refusal: unknown) => {
    const holdingText = t('dev.quickReset.initialize.holding', {
      module: name,
      reason: ports.failure(refusal),
    })
    if (module.module_type === ROBOT_TYPE) throw new StepFailure(holdingText)
    const started = ports.now()
    let now = (await ports.listModules()).find((m) => m.module_id === module.module_id)
    const executing = (m: ModuleSnapshot | undefined) => {
      const id = m?.current_execution?.execution_id
      return typeof id === 'string' && id !== ''
    }
    while (executing(now) && ports.now() - started < QUICK_RELEASE_SETTLE_MS) {
      await ports.sleep(QUICK_IDLE_POLL_MS)
      now = (await ports.listModules()).find((m) => m.module_id === module.module_id)
    }
    if (now === undefined) throw new StepFailure(holdingText)
    if (executing(now))
      throw new StepFailure(t('dev.quickReset.forget.executing', { module: name }))
    if (now.state === HANDOFF)
      throw new StepFailure(t('dev.quickReset.initialize.handoff', { module: name }))
    const grips = Array.isArray(now.grips) ? now.grips : []
    const held = grips.filter((grip) => grip.state === GRIP_HOLDING).map((grip) => grip.robot_id)
    if (held.length === 0)
      throw new StepFailure(
        t('dev.quickReset.forget.none', {
          module: name,
          grips:
            grips.length === 0
              ? '—'
              : grips.map((grip) => `${grip.robot_id} ${String(grip.state)}`).join(', '),
        }),
      )
    running(
      'initialize',
      t('dev.quickReset.forget.running', { module: name, robots: held.join(', ') }),
    )
    for (const robot of held) {
      try {
        await ports.forgetGrips(
          module.module_id,
          robot,
          reason(serverText('dev.quickReset.forget.reason', { module: reasonName(module), robot })),
        )
      } catch (error) {
        throw new StepFailure(
          t('dev.quickReset.forget.refused', { module: name, robot, reason: ports.failure(error) }),
        )
      }
    }
    /*
     * 🔴 「지워졌다」는 다시 읽은 L2 미러가 말한다 — 200 응답의 스냅샷은 아직 `holding` 이다(미러는 모듈 발행으로 늦게
     *    온다). 곧바로 초기화하면 L2 가 옛 미러로 다시 `robot_holding_tray` 를 낸다(쵸단 실측 2026-09-29 17:30 —
     *    forget 200 본문 `cent.cart-1 holding` · 5ms 뒤 초기화 409 · 그 뒤 `GET /modules` 는 `at_target`).
     */
    const since = ports.now()
    for (;;) {
      await ports.sleep(QUICK_IDLE_POLL_MS)
      const after = (await ports.listModules()).find((m) => m.module_id === module.module_id)
      const left = (Array.isArray(after?.grips) ? after.grips : [])
        .filter((grip) => grip.state === GRIP_HOLDING && held.includes(grip.robot_id))
        .map((grip) => grip.robot_id)
      if (left.length === 0) return
      if (ports.now() - since >= QUICK_RELEASE_SETTLE_MS)
        throw new StepFailure(
          t('dev.quickReset.forget.stillHolding', {
            module: name,
            seconds: String(Math.round(QUICK_RELEASE_SETTLE_MS / 1000)),
            robots: left.join(', '),
          }),
        )
    }
  }

  const steps: Record<QuickResetStep, () => Promise<void>> = {
    /* 0 ── 서버 준비 점검 — 🔴 쓰기 «앞»이다. 여기서 멈추면 제어락도 안 잡는다 */
    ready: async () => {
      running('ready', t('dev.quickReset.step.ready'))
      const readiness = await ports.readReadiness()
      if (readiness.kind === 'unreadable')
        throw new StepFailure(t('dev.quickReset.ready.unreadable', { reason: readiness.reason }))
      const problems = readinessProblems(readiness.checks)
      if (problems.length > 0)
        throw new StepFailure(t('dev.quickReset.ready.notReady', { checks: problems.join(' · ') }))
      // 🔴 스케줄러는 권위 창구로 — `ticking` 만 통과 · `terminated` 는 락 뒤에 해제 · 그 밖은 멈춘다
      let scheduler: SchedulerState
      try {
        scheduler = await ports.readScheduler()
      } catch (error) {
        throw new StepFailure(`/scheduler ${ports.failure(error)}`)
      }
      if (scheduler.state === SCHEDULER_TERMINATED) schedulerTerminated = true
      else if (scheduler.state !== SCHEDULER_TICKING)
        throw new StepFailure(
          t('dev.quickReset.ready.scheduler', { state: schedulerText(scheduler) }),
        )
      done('ready', t('dev.quickReset.step.ready'))
    },

    /* 1 ── 제어락 */
    lock: async () => {
      if (ports.lockHeld()) return skipped('lock', t('dev.quickReset.lock.held'))
      running('lock', t('dev.quickReset.lock.running'))
      try {
        await ports.acquireLock()
      } catch (error) {
        // 🔴 남이 쥐고 있으면(409) 강제로 뺏지 않는다 — 다른 세션의 조작을 말없이 끊는 일이다
        throw new StepFailure(
          errorCodeOf(error) === 'CONTROL_LOCK_HELD'
            ? t('dev.quickReset.lock.other', { reason: ports.failure(error) })
            : ports.failure(error),
        )
      }
      done('lock', t('dev.quickReset.lock.done'))
    },

    /* 2 ── 스케줄러 종착 해제 — 0단계가 `terminated` 를 읽었을 때만 · 한 번만 보낸다 */
    scheduler: async () => {
      if (!schedulerTerminated) return skipped('scheduler', t('dev.quickReset.scheduler.none'))
      let now: SchedulerState
      try {
        now = await ports.readScheduler()
      } catch (error) {
        throw new StepFailure(`/scheduler ${ports.failure(error)}`)
      }
      if (now.state === SCHEDULER_TICKING)
        return done('scheduler', t('dev.quickReset.scheduler.done'))
      if (now.state !== SCHEDULER_TERMINATED)
        throw new StepFailure(t('dev.quickReset.ready.scheduler', { state: schedulerText(now) }))
      // 🔴 서버가 말하지 않으면 보내지 않는다 — `reset_allowed` 가 종착 · 제어락 · 역할을 같이 본다
      if (!now.reset_allowed)
        throw new StepFailure(
          t('dev.quickReset.scheduler.notAllowed', {
            state: schedulerText(now),
            reason: now.reset_off_reason ?? '',
          }),
        )
      running('scheduler', t('dev.quickReset.scheduler.running', { state: schedulerText(now) }))
      try {
        await ports.resetScheduler(reason(serverText('dev.quickReset.scheduler.reason')))
      } catch (error) {
        throw new StepFailure(ports.failure(error))
      }
      // 🔴 「풀렸다」는 다시 읽은 `state` 가 말한다 — 루프가 다음 주기에 권위 표를 읽는다
      const started = ports.now()
      for (;;) {
        await ports.sleep(QUICK_IDLE_POLL_MS)
        try {
          now = await ports.readScheduler()
        } catch (error) {
          throw new StepFailure(`/scheduler ${ports.failure(error)}`)
        }
        if (now.state === SCHEDULER_TICKING)
          return done('scheduler', t('dev.quickReset.scheduler.done'))
        if (ports.now() - started >= QUICK_SCHEDULER_SETTLE_MS)
          throw new StepFailure(
            t('dev.quickReset.scheduler.stillDown', {
              seconds: String(Math.round(QUICK_SCHEDULER_SETTLE_MS / 1000)),
              state: schedulerText(now),
            }),
          )
      }
    },

    /* 3 ── 종착 안 한 작업 */
    batches: async () => {
      const active = await ports.listActiveBatches()
      if (active.length === 0) return skipped('batches', t('dev.quickReset.batches.none'))
      const ids = new Set(active.map((batch) => batch.batch_id))
      // 🔴 10단계 진입의 자 — 종착시키기 «전»에 그 작업의 시작 시각과 붙어 있던 실행을 적는다
      for (const batch of active) {
        const at = serverTime(batch.started_at) ?? serverTime(batch.created_at)
        if (at !== undefined) trace.since = Math.min(trace.since ?? at, at)
      }
      for (const module of await ports.listModules()) {
        const current = module.current_execution
        if (
          typeof current?.execution_id === 'string' &&
          current.execution_id !== '' &&
          typeof current.batch_id === 'string' &&
          ids.has(current.batch_id)
        )
          trace.executions.add(current.execution_id)
      }
      let count = 0
      for (const batch of active) {
        const how = terminationOf(batch)
        const id = shortId(batch.batch_id)
        // 🔴 서버 집합이 말하지 않으면 보내지 않는다 — 상태 이름으로 짐작하지 않는다
        if (how === undefined)
          throw new StepFailure(
            t('dev.quickReset.batches.notAllowed', { batch: id, status: String(batch.status) }),
          )
        running(
          'batches',
          t('dev.quickReset.batches.running', { batch: id, how: t(`dev.quickReset.how.${how}`) }),
        )
        const why = reason(serverText('dev.quickReset.batches.reason', { batch: id }))
        try {
          if (how === 'stop') await ports.stopBatch(batch.batch_id, why)
          else await ports.discardBatch(batch.batch_id, why)
        } catch (error) {
          throw new StepFailure(`${id} ${ports.failure(error)}`)
        }
        count += 1
      }
      // 🔴 「종착했다」는 다시 읽은 서버 목록이 말한다
      const left = await ports.listActiveBatches()
      if (left.length > 0)
        throw new StepFailure(
          t('dev.quickReset.batches.left', {
            batches: left
              .map((batch) => `${shortId(batch.batch_id)}(${String(batch.status)})`)
              .join(', '),
          }),
        )
      done('batches', t('dev.quickReset.batches.done', { count: String(count) }))
    },

    /* 4 ── 대기 중 반출 확약 */
    unloads: async () => {
      const pending = await ports.listPendingUnloads()
      if (pending.length === 0) return skipped('unloads', t('dev.quickReset.unloads.none'))
      running('unloads', t('dev.quickReset.unloads.running', { count: String(pending.length) }))
      for (const unload of pending) {
        try {
          await ports.confirmUnload(String(unload.unload_id))
        } catch (error) {
          throw new StepFailure(`${String(unload.unload_id)} ${ports.failure(error)}`)
        }
      }
      done('unloads', t('dev.quickReset.unloads.done', { count: String(pending.length) }))
    },

    /* 9 ── 트레이 자리 정리 — 🔴 내려놓기(8) «뒤»에 돈다(`QUICK_RESET_ORDER`) */
    prepare: async () => {
      const modules = activeModules(await ports.listModules())
      const occupied = modules.flatMap((module) => occupiedPointsOf(module.handoff_occupancy))
      if (occupied.length === 0) return skipped('prepare', t('dev.quickReset.prepare.none'))
      /*
       * 🔴 **남길 작업이 있으면 서버 정리를 보내지 않는다**(2026-10-22 eric 「그냥 시작 버튼만 눌렀어」 — [시작]이 그 작업을 중단시켰다).
       *    `POST /modules/prepare` 는 비종착 작업을 «전부» 종착시키고(`module_prepare.prepare` ①) 뺄 작업을 받는 칸이 없다
       *    (`IrreversibleCommandEnvelope` = `command_id` · `reason`). 점유는 대개 그 작업이 방금 확약한 자기 트레이다 —
       *    판정은 사전 점검(`HANDOFF_POINT_OCCUPIED_AT_START`)에 맡긴다.
       */
      if (keep !== undefined)
        return skipped('prepare', t('dev.quickReset.prepare.kept', { points: occupied.join(', ') }))
      // 🔴 게이트를 그대로 거친다 — 작업 목록도 다시 받은 서버 값이다
      const blocked = ports.prepareBlocked(await ports.listActiveBatches())
      if (blocked !== undefined) throw new StepFailure(blocked)
      running('prepare', t('dev.quickReset.prepare.running', { points: occupied.join(', ') }))
      try {
        await ports.prepare(
          reason(serverText('dev.quickReset.prepare.reason', { points: occupied.join(', ') })),
        )
      } catch (error) {
        throw new StepFailure(ports.failure(error))
      }
      /*
       * 🔴 「풀렸다」는 다시 읽은 점유가 말한다 — 모듈이 확약을 받아도 미러는 구독으로 늦게 온다. 곧바로 작업을 만들면
       *    사전 점검이 옛 미러로 `HANDOFF_POINT_OCCUPIED_AT_START`(fail)를 낸다(실측 2026-09-29 d9f83193 — 정리 1초 뒤
       *    점검이 «05:04 갱신» 점유로 막았고, 몇 초 뒤 다시 재니 통과). 상한 안에서만 다시 읽는다.
       */
      const started = ports.now()
      for (;;) {
        const left = activeModules(await ports.listModules())
          .flatMap((module) => occupiedPointsOf(module.handoff_occupancy))
          .filter((point) => occupied.includes(point))
        if (left.length === 0) break
        if (ports.now() - started >= QUICK_RELEASE_SETTLE_MS)
          throw new StepFailure(
            t('dev.quickReset.prepare.left', {
              seconds: String(Math.round(QUICK_RELEASE_SETTLE_MS / 1000)),
              points: left.join(', '),
            }),
          )
        await ports.sleep(QUICK_IDLE_POLL_MS)
      }
      done('prepare', t('dev.quickReset.prepare.done', { points: occupied.join(', ') }))
    },

    /* 5 ── 모듈에 남은 실행 취소 */
    executions: async () => {
      const holding = activeModules(await ports.listModules()).flatMap((module) => {
        const id = module.current_execution?.execution_id
        return typeof id === 'string' && id !== '' ? [{ module, executionId: id }] : []
      })
      if (holding.length === 0) return skipped('executions', t('dev.quickReset.executions.none'))
      const names = holding.map(({ module }) => ports.moduleName(module)).join(', ')
      running('executions', t('dev.quickReset.executions.running', { modules: names }))
      for (const { module, executionId } of holding) {
        trace.executions.add(executionId)
        try {
          await ports.cancelExecution(
            module.module_id,
            executionId,
            reason(serverText('dev.quickReset.executions.reason')),
          )
        } catch (error) {
          throw new StepFailure(`${ports.moduleName(module)} ${ports.failure(error)}`)
        }
      }
      done('executions', t('dev.quickReset.executions.done', { modules: names }))
    },

    /*
     * 6 ── 안전 해제 — `GET /safety` 가 `normal` · `cleared:true` 가 아니면 `POST /safety/clear` 를 «한 번».
     * 🔴 거절(물리 가드 · 종착 · 살아 있는 모듈이 못 풂)은 그 사유로 멈춘다 — 다시 보내지 않는다.
     * 🔴 「풀렸다」는 다시 읽은 `GET /safety` 가 말한다 — 200 을 받았다고 정상으로 적지 않는다.
     */
    safety: async () => {
      const read = async (): Promise<SafetyState> => {
        try {
          return await ports.readSafety()
        } catch (error) {
          throw new StepFailure(`/safety ${ports.failure(error)}`)
        }
      }
      let now = await read()
      if (safetySettled(now)) return skipped('safety', t('dev.quickReset.safety.none'))
      running('safety', t('dev.quickReset.safety.running', { state: now.state }))
      const live = new Map(
        activeModules(await ports.listModules()).map((module) => [
          module.module_id,
          ports.moduleName(module),
        ]),
      )
      let response: SafetyClearResponse
      try {
        response = await ports.clearSafety()
      } catch (error) {
        const parts = [ports.failure(error)]
        const blocked = unclearedModules(contextList(error, 'results'), live)
        if (blocked.length > 0)
          parts.push(t('dev.quickReset.safety.blocked', { modules: blocked.join(', ') }))
        const unresolved = contextList(error, 'unresolved').filter(
          (token): token is string => typeof token === 'string' && token !== '',
        )
        if (unresolved.length > 0)
          parts.push(t('dev.quickReset.safety.unresolved', { conditions: unresolved.join(', ') }))
        throw new StepFailure(t('dev.quickReset.safety.refused', { reason: parts.join(' · ') }))
      }
      // 🔴 서버가 성립시켰어도 살아 있는 모듈이 못 풀었다고 적혀 있으면 멈춘다(서버 판정과 같은 자 — 방어선 한 겹)
      const blocked = unclearedModules(response.results ?? [], live)
      if (blocked.length > 0)
        throw new StepFailure(
          t('dev.quickReset.safety.refused', {
            reason: t('dev.quickReset.safety.blocked', { modules: blocked.join(', ') }),
          }),
        )
      const started = ports.now()
      for (;;) {
        now = await read()
        if (safetySettled(now)) return done('safety', t('dev.quickReset.safety.done'))
        if (ports.now() - started >= QUICK_SAFETY_SETTLE_MS) {
          const conditions = (now.unresolved_conditions ?? []).join(' · ')
          throw new StepFailure(
            t('dev.quickReset.safety.stillHeld', {
              seconds: String(Math.round(QUICK_SAFETY_SETTLE_MS / 1000)),
              state: now.state,
              conditions: conditions === '' ? '—' : conditions,
            }),
          )
        }
        await ports.sleep(QUICK_IDLE_POLL_MS)
      }
    },

    /*
     * 7 ── 이송 격리 — 「한 번에 풀기」(`IFC-RO-110`). 머리말 「이송 격리(7)」.
     * 🔴 격리가 없으면 보내지 않는다 — 이 단계의 일은 격리다(다른 풀 항목만 있으면 앞 · 뒤 단계 몫).
     * 🔴 파지를 모르는 격리도 같이 푼다(eric 2026-10-21 「무조건 시작」 · 머리말 「이송 격리(7)」).
     * 🔴 「풀렸다」는 다시 읽은 목록이 말한다 — 200 이라도 항목별 `done` 이 거짓이면 멈춘다.
     */
    quarantine: async () => {
      const count = await releaseQuarantines('quarantine')
      if (count === 0) return skipped('quarantine', t('dev.quickReset.quarantine.none'))
      done('quarantine', t('dev.quickReset.quarantine.done', { count: String(count) }))
    },

    /* 8 ── HANDOFF 에 쥔 채 남은 트레이 내려놓기 — 수동 이송 `place_held`(v0.9.1 · 단건 release 는 state 를 안 바꾼다) */
    release: async () => {
      const handoff = async () =>
        activeModules(await ports.listModules()).filter((module) => module.state === HANDOFF)
      const executing = hasExecution
      let stuck = await handoff()
      if (stuck.length === 0) return skipped('release', t('dev.quickReset.release.none'))
      // 🔴 5단계 취소가 가라앉기를 «상한 안에서만» 기다린다 — 이 단계 안의 반복이다
      const started = ports.now()
      while (stuck.some(executing) && ports.now() - started < QUICK_RELEASE_SETTLE_MS) {
        running(
          'release',
          t('dev.quickReset.release.settling', {
            modules: stuck
              .filter(executing)
              .map((module) => ports.moduleName(module))
              .join(', '),
          }),
        )
        await ports.sleep(QUICK_IDLE_POLL_MS)
        stuck = await handoff()
      }
      const placed: string[] = []
      for (const module of stuck) {
        const name = ports.moduleName(module)
        if (executing(module))
          throw new StepFailure(t('dev.quickReset.release.executing', { module: name }))
        /*
         * 🔴 쥐었나 · 목적 자리가 비었나 · 락 · 발행 게이트는 **서버가 판정한다**(후보 목록). 꺼져 있으면 그 사유로 멈춘다 —
         *    쥔 것이 없는 HANDOFF(`robot_not_holding`) · 파지 모름(`grip_uncertain`)도 여기서 서버 문구로 선다.
         */
        /*
         * ⚠ 파지 미러는 상태보다 늦게 온다 — HANDOFF 로 든 직후 몇 초는 `grips` 가 아직 `at_target` 이라 서버가
         *   `robot_not_holding` 으로 끈다(실측 2026-09-29 13:50:27 · 14:12:32). 꺼져 있으면 상한 안에서만 다시 읽는다.
         */
        const readOptions = async (): Promise<ManualTransferOptions> => {
          try {
            return await ports.readPlaceOptions()
          } catch (error) {
            throw new StepFailure(`${name} /transfers/manual/options ${ports.failure(error)}`)
          }
        }
        let choice = (await readOptions()).place_held
        const asked = ports.now()
        while (!choice.enabled && ports.now() - asked < QUICK_RELEASE_SETTLE_MS) {
          running('release', t('dev.quickReset.release.checking', { module: name }))
          await ports.sleep(QUICK_IDLE_POLL_MS)
          choice = (await readOptions()).place_held
        }
        if (!choice.enabled) {
          const refused = t('dev.quickReset.release.refused', {
            module: name,
            reason: verdictText(choice),
          })
          /*
           * 🔴 남기는 작업 «자신»이 내려놓기를 막는가 — 서버는 종착 안 한 배치가 하나라도 있으면 수동 이송을 끈다(`created` 포함 ·
           *    `manual_transfer.facts.common`). 그 작업이 유일한 미종착 작업이면 끊지 않고 시작 창으로 넘긴다(결과 `deferred`).
           */
          if (keep !== undefined) {
            // 🔴 원래 창구로 읽는다 — `ports.listActiveBatches` 는 keep 모드에서 그 작업을 빼고 돌려준다
            const open = await basePorts.listActiveBatches()
            if (open.length > 0 && open.every((batch) => batch.batch_id === keep))
              throw new StepFailure(
                t('dev.quickReset.release.keptBatch', { module: name, reason: refused }),
                'heldTray',
              )
          }
          throw new StepFailure(refused)
        }
        const targets = choice.targets.filter((target) => target.enabled)
        // 서버 순서의 첫 켜진 후보. 🔴 켜진 후보 밖은 고르지 않는다
        const dest = targets[0]
        if (dest === undefined)
          throw new StepFailure(t('dev.quickReset.release.noTarget', { module: name }))
        const point = dest.dest.point_id
        running('release', t('dev.quickReset.release.running', { module: name, point }))
        let job: ManualTransferJob
        try {
          job = await ports.placeHeld(
            point,
            reason(
              serverText('dev.quickReset.release.reason', { module: reasonName(module), point }),
            ),
          )
        } catch (error) {
          throw new StepFailure(`${name} ${ports.failure(error)}`)
        }
        // 🔴 「놓았다」는 잡의 `status` 가 말한다 — 걸음은 스케줄러 틱이 민다
        const since = ports.now()
        while (job.status === JOB_RUNNING) {
          if (ports.now() - since >= QUICK_PLACE_TIMEOUT_MS)
            throw new StepFailure(
              t('dev.quickReset.release.timeout', {
                module: name,
                seconds: String(Math.round(QUICK_PLACE_TIMEOUT_MS / 1000)),
                said: job.said,
              }),
            )
          running('release', t('dev.quickReset.release.waiting', { module: name, said: job.said }))
          await ports.sleep(QUICK_IDLE_POLL_MS)
          try {
            job = await ports.readManualTransfer(job.job_id)
          } catch (error) {
            throw new StepFailure(`${name} /transfers/manual ${ports.failure(error)}`)
          }
        }
        if (job.status !== JOB_DONE)
          throw new StepFailure(
            t('dev.quickReset.release.halted', {
              module: name,
              status: job.status,
              said: job.said,
            }),
          )
        /*
         * 🔴 `done` 은 «놓았다»까지다 — 6축은 그 뒤 대기 자리 복귀(`to_park`)를 한 걸음 더 돈다(2026-10-13 시연 사고 ·
         *    `QUICK_EXEC_SETTLE_MS` 머리말). 그 로봇의 실행이 빠질 때까지 기다리고, 잡을 한 번 더 읽어 그 사이 `halted` 로
         *    바뀌지 않았는지 본다 — 바뀌었으면 이송이 열린 채라 다음 작업이 막힌다.
         */
        await settleExecutions('release', {
          only: (m) => m.module_id === module.module_id,
          strict: true,
        })
        try {
          job = await ports.readManualTransfer(job.job_id)
        } catch (error) {
          throw new StepFailure(`${name} /transfers/manual ${ports.failure(error)}`)
        }
        if (job.status !== JOB_DONE)
          throw new StepFailure(
            t('dev.quickReset.release.halted', {
              module: name,
              status: job.status,
              said: job.said,
            }),
          )
        placed.push(`${name} → ${point}`)
      }
      done('release', t('dev.quickReset.release.done', { placed: placed.join(', ') }))
    },

    /* 10 ── 팔 이동 자세 복귀 */
    arm: async () => {
      const modules = await ports.listModules()
      /*
       * 🔴 진입 — **이번 회차에 종착시킨 작업과 관련된** 팔 거절 알람이 있는 6축만. 8단계 내려놓기는 규칙 걸음이라 팔까지
       *    뺀다(실측 2026-09-29 — 잡 `done` 뒤 로봇 IDLE · 다음 작업의 이송이 거절 없이 돌았다) — 그것만으로는 안 들어간다.
       *    활성 목록에 옛 `ARM_NOT_IN_TRAVEL_POSE` 가 남아 있다는 것만으로 들어가지 않는다(2026-09-29 11:46 · 11:48 —
       *    09-28 알람이 ack 없이 남아 누를 때마다 헛걸음 · 거절 알람이 한 번씩 쌓였다). 옛 알람은 자동 ack 하지 않는다(DP-02).
       *    종착시킨 작업이 없으면(`trace` 빈 채) 알람으로는 안 들어간다.
       */
      const robots: ModuleSnapshot[] = []
      for (const module of activeModules(modules)) {
        if (module.module_type !== ROBOT_TYPE) continue
        if (trace.since === undefined && trace.executions.size === 0) continue
        const alarms = await ports.listRobotAlarms(module.module_id)
        if (alarms.some((alarm) => armAlarmBelongsToRun(alarm, trace))) robots.push(module)
      }
      if (robots.length === 0) return skipped('arm', t('dev.quickReset.arm.none'))
      const candidates = armCandidates(activeModules(modules))
      if (candidates.length === 0)
        throw new StepFailure(
          t('dev.quickReset.arm.ambiguous', { points: t('dev.quickReset.arm.noPoint') }),
        )
      for (const robot of robots) {
        const name = ports.moduleName(robot)
        // 실행이 남아 움직이는 중이면 IDLE 로 온 뒤에 움직인다
        await waitIdle('arm', robot.module_id, name)
        /** 시도한 후보와 그 거절 코드 — 「그 후보는 틀렸다」는 두 코드 모두 같다(`AUX_STATION_CODE` 머리말) */
        const tried: { point: string; code: string }[] = []
        let found: string | undefined
        for (const point of candidates) {
          running('arm', t('dev.quickReset.arm.running', { module: name, point, label: 'above' }))
          const code = await moveAndCheck(robot, name, { point_id: point, stage: 'above' })
          if (code === undefined) {
            found = point
            break
          }
          tried.push({ point, code })
        }
        if (found === undefined) {
          const list = tried.map(({ point, code }) => `${point}(${code})`).join(', ')
          /*
           * 🔴 전부 축 거절이면 «팔이 시도한 어느 지점 안에도 없다»까지만 말한다 — 팔이 at(P) 에 서려면 축이
           *    station(P) 에 있어야 했고 축은 팔이 이동 자세일 때만 움직인다. 팔이 이미 이동 자세·파킹이거나 후보 밖
           *    지점에 있을 수 있다. 빠져나올 순서를 지어내지 않는다(보고 2026-09-29).
           */
          throw new StepFailure(
            tried.every(({ code }) => code === AUX_STATION_CODE)
              ? t('dev.quickReset.arm.auxOnly', { tried: list })
              : t('dev.quickReset.arm.notFound', { tried: list }),
          )
        }
        running(
          'arm',
          t('dev.quickReset.arm.running', { module: name, point: found, label: 'travel' }),
        )
        const travelRefused = await moveAndCheck(robot, name, { point_id: ROBOT_TRAVEL_POINT })
        if (travelRefused !== undefined)
          throw new StepFailure(
            t('dev.quickReset.arm.refused', { module: name, label: 'travel', code: travelRefused }),
          )
        done('arm', t('dev.quickReset.arm.done', { modules: name, point: found }))
      }
    },

    /* 11 ── IDLE 아닌 모듈 초기화 */
    initialize: async () => {
      /*
       * 🔴 실행 중인 동작을 초기화로 끊지 않는다 — 이송의 마지막 걸음(6축 복귀 등)이 남아 있으면 그게 끝나기를 먼저
       *    기다린다(2026-10-13 시연 사고 · `QUICK_EXEC_SETTLE_MS`). 상한이 지나도 남으면 종전대로 초기화가 끊는다.
       */
      await settleExecutions('initialize', { strict: false })
      const targets = busyModules(await ports.listModules())
      if (targets.length === 0) return skipped('initialize', t('dev.quickReset.initialize.none'))
      for (const module of targets) {
        const name = ports.moduleName(module)
        // 🔴 단독 모드 · 트레이 쥔 로봇은 보내지 않고 멈춘다 — 서버도 거절하거나(409) 떨어뜨린다
        if (module.control_owner === 'local')
          throw new StepFailure(t('dev.quickReset.initialize.local', { module: name }))
        if (module.state === HANDOFF)
          throw new StepFailure(t('dev.quickReset.initialize.handoff', { module: name }))
        // 🔴 `ERROR` 는 초기화를 안 받는다(`MODULE_IN_ERROR`) — 복구 개시 한 사이클이 출구다
        if (module.state === ERROR_STATE) {
          await recover(module, name)
          continue
        }
        running(
          'initialize',
          t('dev.quickReset.initialize.running', { module: name, state: module.state }),
        )
        /* 🔵 원심이 중단 뒤 서 있으면 로터 정지 확인을 싣는다(스냅샷 `exit_blockers` 가 말할 때만 · 2026-10-23 실측 — 없으면 409) */
        const rotor = {
          rotorStopConfirmed: module.exit_blockers?.includes('rotor_stop_unconfirmed') === true,
        }
        for (let attempt = 1; ; attempt += 1) {
          try {
            await ports.initialize(module.module_id, rotor)
            break
          } catch (error) {
            const code = errorCodeOf(error)
            // 읽은 뒤에 ERROR 로 떨어졌다 — 같은 출구
            if (code === IN_ERROR_CODE) {
              await recover(module, name)
              break
            }
            /*
             * 🔴 쥔 로봇은 기다려도 안 풀린다 — 같은 초기화를 되풀이하지 않는다. 내려놓을 길이 없는 파지 «기억»
             *    (모듈 안 로봇 · 실행 없음)이면 기억을 지우고 초기화를 «한 번만» 다시 보낸다. 그 밖은 멈춘다(`forgetHeld`).
             */
            if (
              code === HOLDING_TRAY_CODE ||
              (code === BUSY_CODE && contextField(error, 'reason') === ROBOT_HOLDING_REASON)
            ) {
              await forgetHeld(module, name, error)
              try {
                await ports.initialize(module.module_id, rotor)
              } catch (again) {
                throw new StepFailure(
                  t('dev.quickReset.forget.initFailed', {
                    module: name,
                    reason: ports.failure(again),
                  }),
                )
              }
              break
            }
            if (code === BUSY_CODE && attempt < QUICK_INIT_ATTEMPTS) {
              await ports.sleep(QUICK_INIT_RETRY_MS)
              continue
            }
            // 🔵 문이 열려 있어 거절 — 닫고 같은 초기화를 다시 보낸다(시도 횟수에 든다)
            if (code === DOOR_OPEN_CODE && attempt < QUICK_INIT_ATTEMPTS) {
              const door = doorOf(error)
              running('initialize', t('dev.quickReset.initialize.closeDoor', { module: name }))
              try {
                await ports.runSequence(
                  module.module_id,
                  'close_door',
                  door === undefined ? {} : { door },
                )
              } catch (closeError) {
                // 🔴 ERROR 면 단건도 안 받는다 — 복구 개시가 먼저다
                if (errorCodeOf(closeError) === IN_ERROR_CODE) {
                  await recover(module, name)
                  break
                }
                throw new StepFailure(`${name} close_door ${ports.failure(closeError)}`)
              }
              await ports.sleep(QUICK_INIT_RETRY_MS)
              continue
            }
            throw new StepFailure(`${name} ${ports.failure(error)}`)
          }
        }
      }
      done(
        'initialize',
        t('dev.quickReset.initialize.done', {
          modules: targets.map((module) => ports.moduleName(module)).join(', '),
        }),
      )
    },

    /* 12 ── 전부 IDLE 이 될 때까지 — 이 단계 안에서만 다시 읽는다 */
    idle: async () => {
      /*
       * 🔴 마지막 관문 — 작업을 만들기 직전에 이송 격리가 남아 있으면 «여기서 푼다»(eric 2026-10-21 「이거 계속 뜨잖아」 · 「무조건 시작」).
       *    7단계 뒤의 내려놓기(8) · 정리(9) · 초기화(11)가 격리를 새로 세울 수 있다. 남겨 두면 새 작업의 첫 이송이 「수동 이송이 그 자원을
       *    쓰고 있다 · 격리 중」으로 막힌다(2026-10-13 시연 사고). ⚠ 종전엔 여기서 멈추고 「다시 누르면 풉니다」를 적었는데, 다시 눌러도
       *    같은 단계들이 또 격리를 세워 늘 같은 자리에서 멈췄다.
       */
      const leftovers = async () => {
        await releaseQuarantines('idle')
      }
      const started = ports.now()
      let left = busyModules(await ports.listModules())
      if (left.length === 0) {
        await leftovers()
        return skipped('idle', t('dev.quickReset.idle.already'))
      }
      for (;;) {
        const elapsed = ports.now() - started
        const status = left
          .map((module) => `${ports.moduleName(module)} ${module.state}`)
          .join(', ')
        if (elapsed >= QUICK_IDLE_TIMEOUT_MS)
          throw new StepFailure(
            t('dev.quickReset.idle.timeout', {
              seconds: String(Math.round(QUICK_IDLE_TIMEOUT_MS / 1000)),
              modules: status,
            }),
          )
        running(
          'idle',
          t('dev.quickReset.idle.running', {
            seconds: String(Math.floor(elapsed / 1000)),
            modules: status,
          }),
        )
        await ports.sleep(QUICK_IDLE_POLL_MS)
        left = busyModules(await ports.listModules())
        if (left.length === 0) {
          await leftovers()
          done(
            'idle',
            t('dev.quickReset.idle.done', {
              seconds: String(Math.floor((ports.now() - started) / 1000)),
            }),
          )
          return
        }
      }
    },
  }

  for (const step of QUICK_RESET_ORDER) {
    try {
      await steps[step]()
    } catch (error) {
      const why = error instanceof StepFailure ? error.reason : ports.failure(error)
      lines = skipRest(
        [
          ...lines.filter((one) => one.step !== step),
          {
            step,
            status: 'failed',
            text: t('dev.quickReset.failed', {
              step: t(`dev.quickReset.step.${step}`),
              reason: why,
            }),
          },
        ],
        step,
      )
      onLines(lines)
      const deferred = error instanceof StepFailure ? error.deferred : undefined
      return { ok: false, step, reason: why, ...(deferred === undefined ? {} : { deferred }) }
    }
  }
  return { ok: true }
}

/** 실행이 붙었나 — 실행 id 가 있거나, 쉬는 상태(IDLE)를 벗어났다(자동 준비가 전부 IDLE 로 맞춘 뒤라 움직임 = 스텝) */
function moving(module: ModuleSnapshot): boolean {
  const id = module.current_execution?.execution_id
  return (typeof id === 'string' && id !== '') || module.state !== IDLE
}

/** 계약 `Batch.status` 원값 — 시작이 받아지면 서버가 곧바로 이 값으로 옮긴다(`FROM_START → running`) */
const RUNNING = 'running'
/**
 * 시작 뒤 «멈췄다»로 읽는 계약 `Batch.status` 원값 — 일시정지 · 중단 · 오류. `completed` 는 멈춤이 아니다.
 * 🔴 판정은 서버가 준 `status` 한 칸이다 — 모듈 상태 · 알람으로 작업이 멈췄다고 추론하지 않는다
 */
const HALTED: ReadonlySet<string> = new Set(['paused', 'aborted', 'error'])

/**
 * 활성 알람 중 **가장 최근 것**(`last_ts` 최대) — `출처 코드`. 서버 목록은 `alarm_id` 순이라 시각으로 다시 고른다.
 * `last_ts` 를 못 읽는 행은 고르지 않는다(순서를 지어내지 않는다). 둘 다 비면 `undefined`
 */
export function latestAlarmText(alarms: readonly Alarm[]): string | undefined {
  let best: { at: number; alarm: Alarm } | undefined
  for (const alarm of alarms) {
    const at = typeof alarm.last_ts === 'string' ? Date.parse(alarm.last_ts) : Number.NaN
    if (Number.isNaN(at)) continue
    if (best === undefined || at > best.at) best = { at, alarm }
  }
  if (best === undefined) return undefined
  const parts = [best.alarm.source, best.alarm.error_code].filter(
    (part): part is string => typeof part === 'string' && part !== '',
  )
  return parts.length === 0 ? undefined : parts.join(' ')
}

/**
 * 시작 직후 멈춘 작업의 한 줄 — 🔴 서버 값 그대로: `halt_reason` → 없으면 최근 활성 알람 → 둘 다 없으면 상태만.
 * 알람은 `halt_reason` 이 빌 때만 읽는다
 */
async function haltedText(
  ports: Pick<QuickResetPorts, 'listActiveAlarms'>,
  batch: Batch,
): Promise<string> {
  const status = batchStatusLabel(batch.status)
  const reason = typeof batch.halt_reason === 'string' ? batch.halt_reason.trim() : ''
  if (reason !== '') return t('dev.quickStart.halted', { status, reason })
  const alarm = latestAlarmText(await ports.listActiveAlarms())
  return alarm === undefined
    ? t('dev.quickStart.haltedBare', { status })
    : t('dev.quickStart.haltedAlarm', { status, alarm })
}

/**
 * **시작 뒤 감시** — 시작 명령이 받아진 «뒤»에 부른다. 멈춤 · 정체면 한 줄, 아니면 `undefined`.
 *
 * - `QUICK_STALL_MS` 안에 대상 모듈(`service_state === 'active'`) 어디에든 실행이 붙으면(실행 id 가 서거나 IDLE 을
 *   벗어나면) 곧바로 끝낸다 — 문구 없음. ⚠ 짧은 스텝이 두 번의 읽기 사이에 끝나면 놓칠 수 있어 IDLE 이탈도 같이 본다
 * - 🔴 같은 간격으로 그 작업(`GET /batches/{id}`)도 읽는다. `paused` · `aborted` · `error` 가 되면 곧바로 끝내고
 *   **멈춘 한 줄**을 적는다(2026-09-29 — 모듈이 첫 블록을 422 로 거절해 시작 1초 뒤 `paused` 였는데 화면이 말이 없었다)
 * - 상한까지 안 붙었고 그 작업이 아직 `running` 이면 정체다. 그 시점 `/readyz` 를 한 번 더 읽어 붙인다
 * 🔴 작업을 끊거나 다시 시작하거나 재개하지 않는다(DP-02) — 알리기만 한다.
 * 🔴 감시 중 조회가 실패하면 조용히 끝낸다 — 「못 봤다」를 「정체」로 적지 않는다
 */
export async function watchStartStall(
  ports: Pick<
    QuickResetPorts,
    'listModules' | 'readBatch' | 'listActiveAlarms' | 'readReadiness' | 'sleep' | 'now'
  >,
  batchId: string,
): Promise<string | undefined> {
  try {
    const started = ports.now()
    for (;;) {
      if (activeModules(await ports.listModules()).some(moving)) return undefined
      const batch = await ports.readBatch(batchId)
      if (HALTED.has(batch.status)) return await haltedText(ports, batch)
      if (ports.now() - started >= QUICK_STALL_MS) {
        if (batch.status !== RUNNING) return undefined
        break
      }
      await ports.sleep(QUICK_STALL_POLL_MS)
    }
    return t('dev.quickStart.stalled', {
      seconds: String(Math.round(QUICK_STALL_MS / 1000)),
      checks: readinessSummary(await ports.readReadiness()),
    })
  } catch {
    return undefined
  }
}
